import i18n from "../i18n";
import { CHALLENGE_ERROR_KEYS } from "./challenge-participants";
import { apiErrorI18nKey } from "./api-errors";
import { withPilotScope, getPilotScope } from "./pilot-scope";
import { fetchWithRetry } from "./fetch-retry";
import type { SupportAction, SupportScreen } from "./support-screens";

// Cookie is httpOnly — JS cannot read it. Auth state is determined by /api/auth/me.
export function clearToken(): void {
  // Legacy cleanup: remove any leftover localStorage tokens from before the cookie migration
  localStorage.removeItem("bizcoach_token");
  localStorage.removeItem("bizcoach_challenge");
}

/** Clé sessionStorage pour l'impersonation rapide ADM (cf. handleMe). */
export const IMPERSONATE_KEY = "bizcoach_impersonate_user_id";

async function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  // FormData → laisser fetch calculer le boundary automatiquement (pas de Content-Type forcé)
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  const headers: Record<string, string> = isFormData
    ? { ...(options.headers as Record<string, string>) }
    : {
        "Content-Type": "application/json",
        ...(options.headers as Record<string, string>),
      };

  // Legacy: send Authorization header if old localStorage token exists (backward compat)
  const legacyToken = localStorage.getItem("bizcoach_token");
  if (legacyToken) {
    headers["Authorization"] = `Bearer ${legacyToken}`;
  }

  // Quick impersonation : si activée, ajout auto du query param sur toutes les
  // routes API (sauf auth/login/logout pour ne pas casser le flow d'auth).
  let finalUrl = url;
  const asUserId = typeof window !== "undefined" ? sessionStorage.getItem(IMPERSONATE_KEY) : null;
  const isAuthLoginRoute = url.includes("/api/auth/magic-link") || url.includes("/api/auth/verify") || url.includes("/api/auth/logout");
  if (asUserId && url.startsWith("/api/") && !isAuthLoginRoute && !url.includes("asUserId=")) {
    finalUrl = `${url}${url.includes("?") ? "&" : "?"}asUserId=${encodeURIComponent(asUserId)}`;
  }

  // Résilience réseau : retente les GET en échec réseau transitoire ("Failed to fetch").
  const response = await fetchWithRetry(finalUrl, { ...options, headers, credentials: "same-origin" });

  // Auto-redirect to /auth on 401 (expired/invalid token)
  if (response.status === 401 && !url.includes("/api/auth/")) {
    clearToken();
    window.location.href = "/auth";
  }

  return response;
}

export const authApi = {
  async requestMagicLink(email: string) {
    const res = await apiFetch("/api/auth/magic-link", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.sendLinkError"));
    // Store challenge in localStorage to bind link to this browser
    if (json.challenge) {
      localStorage.setItem("bizcoach_challenge", json.challenge);
    }
    return json.message;
  },

  async verifyMagicLink(token: string) {
    const challenge = sessionStorage.getItem("bizcoach_challenge") || localStorage.getItem("bizcoach_challenge");
    const res = await apiFetch("/api/auth/verify", {
      method: "POST",
      body: JSON.stringify({ token, challenge }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.invalidOrExpired"));
    // Clean up challenge
    sessionStorage.removeItem("bizcoach_challenge");
    localStorage.removeItem("bizcoach_challenge");
    // Clean up legacy localStorage token
    localStorage.removeItem("bizcoach_token");
    // JWT is now set as httpOnly cookie by the server
    return json.user;
  },

  async me() {
    const res = await apiFetch("/api/auth/me");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.notAuthenticated"));
    return json.user;
  },

  async updateProfile(data: {
    civility?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    profilePicture?: string;
    linkedinUrl?: string;
    language?: string;
  }) {
    const res = await apiFetch("/api/auth/me", {
      method: "PUT",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.updateError"));
    return json.user;
  },
  /** Alias historique de `updateProfile` (OnboardingPage). */
  async updateMe(data: { language?: string }) { return this.updateProfile(data); },

  async logout() {
    await apiFetch("/api/auth/logout", { method: "POST" });
    // Clean up any legacy tokens
    localStorage.removeItem("bizcoach_token");
    localStorage.removeItem("bizcoach_challenge");
    sessionStorage.removeItem("bizcoach_challenge");
  },

  /**
   * Compte et périmètre passés EXPLICITEMENT par l'appelant (`ReportingTab` en mode Pilot les lit dans
   * l'en-tête Pilot) : jamais hérités en silence du choix fait dans Pilot — la page « Mon équipe »
   * du RH affichait sinon une seule équipe sans le dire (audit du lot 4, 29/09/2026).
   */
  async getReporting(params?: { from?: string; to?: string; userId?: string; simulatorId?: string; accountId?: string; scope?: string; pilot?: boolean }) {
    const qs = new URLSearchParams();
    if (params?.from) qs.set("from", params.from);
    if (params?.to) qs.set("to", params.to);
    if (params?.userId) qs.set("userId", params.userId);
    if (params?.simulatorId) qs.set("simulatorId", params.simulatorId);
    if (params?.accountId) qs.set("accountId", params.accountId);
    if (params?.scope) qs.set("scope", params.scope);
    if (params?.pilot) qs.set("pilot", "1");
    const res = await apiFetch(`/api/reporting?${qs.toString()}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement reporting");
    return json;
  },

  async getAgents() {
    const res = await apiFetch("/api/agents");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.loadAgentsError"));
    return json.agents;
  },
};

// Packs de sessions one-shot — configuration du périmètre simulateur (côté client).
export interface OneShotPack {
  id: string;
  licenceName: string;
  licenceCode: string;
  tier: string | null;
  maxSimulators: number | null;
  rateLimitSessions: number | null;
  expiresAt: string | null;
  simulatorIds: string[];
  isPremium: boolean;
  needsConfig: boolean;
}

export interface PackSimulatorOption {
  id: string;
  name: string;
  description?: string | null;
  descriptionFr?: string | null;
  descriptionEn?: string | null;
  icon?: string | null;
}

export const packsApi = {
  async getOneShot(accountId?: string): Promise<{ packs: OneShotPack[]; availableSimulators: PackSimulatorOption[] }> {
    const res = await apiFetch(accountUrl("/api/account/oneshot-packs", accountId));
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement des packs");
    return json;
  },
  async setSimulators(grantId: string, simulatorIds: string[]) {
    const res = await apiFetch("/api/account/oneshot-packs", {
      method: "PUT",
      body: JSON.stringify({ grantId, simulatorIds }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur configuration du pack");
    return json;
  },
};

// --- Pseudo + Avatar (Challenges V2) ---
export const pseudoApi = {
  async setPseudo(pseudo: string, avatarSeed?: string) {
    const res = await apiFetch("/api/users/me/pseudo", {
      method: "POST",
      body: JSON.stringify({ pseudo, avatarSeed }),
    });
    const json = await res.json();
    if (!res.ok) {
      const err: any = new Error(json.error || "Erreur pseudo");
      err.errorCode = json.errorCode;
      err.suggestions = json.suggestions;
      err.suggestion = json.suggestion;
      throw err;
    }
    return json;
  },

  async regenerateAvatar() {
    const res = await apiFetch("/api/users/me/avatar", { method: "PUT" });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur avatar");
    return json;
  },

  // Marque le tour guidé de découverte comme vu/passé (par utilisateur).
  async markTourComplete() {
    const res = await apiFetch("/api/users/me/tour-complete", { method: "POST" });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || "Erreur tour");
    return json;
  },
};

// --- Badges (Challenges V2) ---
export const badgesApi = {
  async getAll() {
    const res = await apiFetch("/api/badges");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur badges");
    return json.badges;
  },

  async create(data: { name: string; description?: string; imageUrl: string }) {
    const res = await apiFetch("/api/badges", { method: "POST", body: JSON.stringify(data) });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur création badge");
    return json.badge;
  },

  async update(id: string, data: { name?: string; description?: string; imageUrl?: string }) {
    const res = await apiFetch(`/api/badges/${id}`, { method: "PUT", body: JSON.stringify(data) });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur mise à jour badge");
    return json.badge;
  },

  async remove(id: string) {
    const res = await apiFetch(`/api/badges/${id}`, { method: "DELETE" });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur suppression badge");
    return json;
  },

  async getMyBadges() {
    const res = await apiFetch("/api/users/me/badges");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur badges");
    return json.badges;
  },
};

// --- Inter challenges admin (Challenges V2) ---
export const interChallengesApi = {
  async getOffers() {
    const res = await apiFetch("/api/admin/inter-challenge-offers");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement offres inter");
    return json.offers;
  },
  async getPersonae() {
    const res = await apiFetch("/api/admin/inter-challenge-personae");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement personae inter");
    return json.personae;
  },
};

export const sessionsApi = {
  /** `team` : historique de l'ÉQUIPE dans Pilot (périmètre de la barre de Pilot, décision 2.3). */
  async getSessions(limit?: number, userId?: string, opts?: { team?: boolean }) {
    const params = new URLSearchParams();
    if (limit) params.set("limit", String(limit));
    if (userId) params.set("userId", userId);
    if (opts?.team) params.set("team", "1");
    const query = params.toString() ? `?${params}` : "";
    const res = await apiFetch(opts?.team ? withPilotScope(`/api/sessions${query}`) : `/api/sessions${query}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.loadSessionsError"));
    return json.sessions;
  },

  /**
   * Historique de l'ÉQUIPE (Pilot › Activité) : les sessions ET les personnes du périmètre servi par
   * le serveur (barre de Pilot), pour un ADM / COO placé chez un client comme pour un encadrant.
   */
  async getTeamSessions(userId: string, limit?: number): Promise<{ sessions: any[]; people: { id: string; name: string }[] }> {
    const params = new URLSearchParams({ team: "1", userId });
    if (limit) params.set("limit", String(limit));
    const res = await apiFetch(withPilotScope(`/api/sessions?${params}`));
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.loadSessionsError"));
    return { sessions: json.sessions ?? [], people: json.people ?? [] };
  },

  async getSessionDetail(id: string) {
    const res = await apiFetch(`/api/sessions/${id}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.loadSessionError"));
    return json.session;
  },
};

export const trainingApi = {
  async createSession(
    simulatorId: string,
    challengeId?: string,
    pathPhaseId?: string,
    // Re-train ciblé (réutilisabilité — Étape 6) : retravaille un critère d'un débrief.
    focus?: { focusCriterionKey: string; sourceSessionId: string },
    // Duel (Étape 8) : rattache la passe au duel.
    duelId?: string,
    // « Tes séances » : la séance choisie par le joueur — soldée par CETTE session (rattrapage compris).
    plannedSessionId?: string,
  ) {
    const res = await apiFetch("/api/sessions", {
      method: "POST",
      body: JSON.stringify({
        simulatorId,
        ...(challengeId && { challengeId }),
        ...(pathPhaseId && { pathPhaseId }),
        ...(focus && { focusCriterionKey: focus.focusCriterionKey, sourceSessionId: focus.sourceSessionId }),
        ...(duelId && { duelId }),
        ...(plannedSessionId && { plannedSessionId }),
      }),
    });
    const json = await res.json();
    if (!res.ok) {
      if (json.error === "SIMULATION_QUOTA_REACHED") {
        const err = new Error(json.message) as Error & { code: string; used: number; max: number };
        err.code = "SIMULATION_QUOTA_REACHED";
        err.used = json.used;
        err.max = json.max;
        throw err;
      }
      // Trial mode — limite de simulations d'essai atteinte (cf. spec/trial-mode-with-coupon.md §4.5)
      if (res.status === 403 && json.error === "TRIAL_LIMIT_REACHED") {
        const err = new Error(json.message) as Error & {
          code: string; requiresDecision?: boolean; declined?: boolean; reason?: string; timeExpired?: boolean;
        };
        err.code = "TRIAL_LIMIT_REACHED";
        err.requiresDecision = json.requiresDecision;
        // Essai DÉJÀ décliné : plus de décision à prendre, donc le rechargement de page ne
        // ferait rien apparaître. Le front doit afficher un message.
        err.declined = json.declined;
        // ⚠️ Motif + `timeExpired` : sans eux, `TrainingPage` n'a que la phrase FRANÇAISE écrite
        // dans `trial-runtime.ts` et l'affiche telle quelle à un lecteur anglophone — alors que
        // les clés traduites existent et que l'écran des exercices les utilise déjà.
        err.reason = json.reason;
        err.timeExpired = json.timeExpired;
        throw err;
      }
      // Rate limiter mensuel/lifetime — cf. spec/rate-limiter-sessions.md
      if (res.status === 402 && json.error === "session_quota_exceeded") {
        const err = new Error(json.message) as Error & {
          code: string; used: number; limit: number; totalPacksRemaining: number;
          ctaUrl?: string; envMode?: string; cycleStart?: string; cycleEnd?: string; userProfileType?: string;
          reason?: string; subscriptionState?: string;
          subscriptionEndedAt?: string | null; subscriptionResumesAt?: string | null;
          subscriptionSource?: "manual";
        };
        // ⚠️ Cette recopie champ par champ est un PIÈGE : un champ ajouté au corps de la réponse
        // 402 et lu par l'écran reste `undefined` s'il n'est pas repris ICI. C'est ce qui a rendu
        // inerte, le 29/09/2026, le message « votre abonnement a pris fin le … » — le serveur
        // l'envoyait, la modale l'attendait, cette ligne manquait. Tout nouveau champ s'ajoute ici.
        err.code = "SESSION_QUOTA_EXCEEDED";
        err.used = json.used;
        err.limit = json.limit;
        err.totalPacksRemaining = json.totalPacksRemaining;
        err.ctaUrl = json.ctaUrl;
        err.envMode = json.envMode;
        err.cycleStart = json.cycleStart;
        err.cycleEnd = json.cycleEnd;
        err.userProfileType = json.userProfileType;
        err.reason = json.reason;
        err.subscriptionState = json.subscriptionState;
        err.subscriptionEndedAt = json.subscriptionEndedAt;
        err.subscriptionResumesAt = json.subscriptionResumesAt;
        err.subscriptionSource = json.subscriptionSource;
        throw err;
      }
      // Challenge en pause : message traduit (le lancement est refusé, décision du 30/09/2026).
      if (json.code && CHALLENGE_ERROR_KEYS[json.code]) throw new Error(i18n.t(CHALLENGE_ERROR_KEYS[json.code]!));
      throw new Error(apiErrorText(json, i18n.t("api.createSessionError")));
    }
    return json.session;
  },

  async getQuota() {
    const res = await apiFetch("/api/account/quota");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement quota");
    return json;
  },

  async buyPack(quantity: number) {
    const res = await apiFetch("/api/account/buy-pack", {
      method: "POST",
      body: JSON.stringify({ quantity }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.buyPack")));
    return json;
  },

  /**
   * Logue le coût d'un événement de session (cf. spec/cost-tracking.md).
   * Best-effort : on swallow les erreurs côté client pour ne pas casser le
   * flow utilisateur (cleanup de session vocale).
   */
  /**
   * Cost dashboard admin (cf. spec/cost-tracking.md §6).
   */
  async getCostDashboard(filters: { from?: string; to?: string; accountId?: string; simulatorId?: string; userId?: string; limit?: number } = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v != null && v !== "") params.set(k, String(v)); });
    const res = await apiFetch(`/api/admin/cost-dashboard${params.toString() ? "?" + params.toString() : ""}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement dashboard coûts");
    return json;
  },

  async getCostDashboardSession(sessionId: string) {
    const res = await apiFetch(`/api/admin/cost-dashboard/session/${sessionId}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur détail session");
    return json.events;
  },

  costDashboardExportUrl(filters: { from?: string; to?: string; accountId?: string; simulatorId?: string; userId?: string } = {}): string {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v != null && v !== "") params.set(k, String(v)); });
    return `/api/admin/cost-dashboard/export${params.toString() ? "?" + params.toString() : ""}`;
  },

  /**
   * Suivi des utilisations — cockpit d'adoption CS/Sales (cf. spec/suivi-utilisations.md).
   */
  async getUsagePortfolio(filters: { from?: string; to?: string } = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v != null && v !== "") params.set(k, String(v)); });
    const res = await apiFetch(`/api/admin/usage/portfolio${params.toString() ? "?" + params.toString() : ""}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement suivi des utilisations");
    return json;
  },

  usagePortfolioExportUrl(filters: { from?: string; to?: string } = {}): string {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v != null && v !== "") params.set(k, String(v)); });
    return `/api/admin/usage/portfolio/export${params.toString() ? "?" + params.toString() : ""}`;
  },

  async getUsageAccountDetail(accountId: string, filters: { from?: string; to?: string } = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v != null && v !== "") params.set(k, String(v)); });
    const res = await apiFetch(`/api/admin/usage/account/${accountId}${params.toString() ? "?" + params.toString() : ""}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement fiche client");
    return json;
  },

  async getUsageUserDetail(userId: string, filters: { from?: string; to?: string } = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v != null && v !== "") params.set(k, String(v)); });
    const res = await apiFetch(`/api/admin/usage/user/${userId}${params.toString() ? "?" + params.toString() : ""}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement fiche utilisateur");
    return json;
  },

  async getUsageSimulatorSessions(accountId: string, simulatorId: string, filters: { from?: string; to?: string } = {}) {
    const params = new URLSearchParams({ accountId, simulatorId });
    Object.entries(filters).forEach(([k, v]) => { if (v != null && v !== "") params.set(k, String(v)); });
    const res = await apiFetch(`/api/admin/usage/simulator-sessions?${params.toString()}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement sessions du simulateur");
    return json;
  },

  async searchUsage(q: string) {
    const res = await apiFetch(`/api/admin/usage/search?q=${encodeURIComponent(q)}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur recherche");
    return json as {
      accounts: { id: string; name: string }[];
      users: { userId: string; firstName: string | null; lastName: string | null; email: string | null; accountId: string | null; accountName: string | null }[];
      sessions: { id: string; createdAt: string; accountId: string | null; accountName: string | null; userId: string | null; firstName: string | null; lastName: string | null; email: string | null; simulatorName: string | null; type: "entrainement" | "parcours" | "challenge"; durationInMinutes: number | null; score: number | null }[];
    };
  },

  async getUsageWatches() {
    const res = await apiFetch(`/api/admin/usage/watches`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement des suivis");
    return json.watches as { clientAccountId: string; channels: any; triggers: any }[];
  },

  async upsertUsageWatch(accountId: string, config: { channels: any; triggers: any }) {
    const res = await apiFetch(`/api/admin/usage/watch/${accountId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(config),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur enregistrement du suivi");
    return json;
  },

  async deleteUsageWatch(accountId: string) {
    const res = await apiFetch(`/api/admin/usage/watch/${accountId}`, { method: "DELETE" });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur suppression du suivi");
    return json;
  },

  // Dossier de Confiance — contenu servi depuis le backend (gated ADM/COO),
  // volontairement PAS dans le bundle front (confidentiel).
  async getTechDoc() {
    const res = await apiFetch(`/api/admin/tech-doc`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement de la documentation");
    return json as {
      version: string; updated: string;
      sections: { id: string; icon: string; ref?: string; title: string; body: string }[];
      releases: { date: string; kind: "feature" | "fix" | "tech"; title: string; items: string[]; scope?: string }[];
      evidence: { title: string; items: { name: string; desc: string; location: string; status: "available" | "partial" | "todo"; link?: { kind: "section" | "tab" | "nav"; target: string } }[] }[];
      settings: Record<string, string>;
      settingsUpdatedAt: string | null;
      settingsFields: { title: string; fields: { key: string; label: string; placeholder: string; multiline?: boolean }[] }[];
    };
  },

  async getTechDocSettings() {
    const res = await apiFetch(`/api/admin/tech-doc/settings`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement des paramètres");
    return json as { data: Record<string, string>; updatedAt: string | null };
  },
  async saveTechDocSettings(data: Record<string, string>) {
    const res = await apiFetch(`/api/admin/tech-doc/settings`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ data }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur enregistrement");
    return json;
  },

  async logSessionCost(payload: {
    sessionId: string;
    eventType: "audio_realtime" | "llm_scenario" | "llm_feedback" | "llm_coach";
    modelName: string;
    tokensInput?: number;
    tokensOutput?: number;
    tokensCached?: number;
    audioInputTokens?: number;
    textInputTokens?: number;
    audioOutputTokens?: number;
    textOutputTokens?: number;
    audioInputSeconds?: number;
    audioOutputSeconds?: number;
    notes?: string;
  }): Promise<void> {
    try {
      await apiFetch("/api/internal/log-session-cost", {
        method: "POST",
        body: JSON.stringify(payload),
      });
    } catch {
      // Silencieux — le cost tracking ne doit pas casser le flow user.
    }
  },

  async updateSession(sessionId: string, data: { durationInMinutes?: number; endTime?: string; numberOfMessages?: number }) {
    const res = await apiFetch(`/api/sessions/${sessionId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.updateSessionError"));
    return json.session;
  },

  async generateScenario(sessionId: string, params: {
    difficulty?: string;
    personaId?: string;
    // Cas de simulation choisi (relation M<->N persona<->cas) : permet de résoudre
    // le bon contexte (offre/secteur/critères) côté scoring sans dépendre de
    // persona.targetId (une persona pouvant appartenir à plusieurs cas).
    targetId?: string;
    // Couture Sales Coach Réel : focus libre transmis à N8N pour orienter la
    // génération du scénario (citation + reformulation + notes du RDV débriefé).
    coachingFocus?: string;
    coachingOfferId?: string;
  }) {
    const res = await apiFetch(`/api/sessions/${sessionId}/scenario`, {
      method: "POST",
      body: JSON.stringify(params),
    });
    const json = await res.json();
    // Refus codé (ex. cas désactivé, `target_archived`) : texte dans la langue de l'écran.
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.generateScenarioError")));
    return json.scenario;
  },

  async getRealtimeToken(sessionId: string, type: "simulator" | "coach") {
    const res = await apiFetch(`/api/sessions/${sessionId}/realtime-token`, {
      method: "POST",
      body: JSON.stringify({ type }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.getTokenError"));
    return json as { agentConfig: { client_secret: { value: string }; model?: string; voice?: string; name?: string; instructions?: string }; audioSessionId: string };
  },

  /**
   * Relaie l'ouverture d'une session vocale au sideband-controller via le back
   * BizCoach (POST /api/sideband). Le back authentifie l'user, vérifie
   * l'ownership de la session, émet un JWT de session court et relaie au
   * contrôleur.
   *
   * Les noms de champs sont alignés EXACTEMENT sur le contrat du handler
   * `src/api/sideband.ts` : { sessionId, call_id, ephemeralKey, simulatorName }.
   * (`call_id` en snake_case côté contrat — c'est le format OpenAI Realtime.)
   *
   * Best-effort / NON bloquant : toute erreur (réseau, contrôleur down, pas de
   * call_id côté appelant) est silencieuse — l'ouverture de session vocale ne
   * doit JAMAIS casser à cause du sideband.
   */
  async postSideband(payload: {
    sessionId: string;
    call_id: string;
    ephemeralKey: string;
    simulatorName?: string;
  }): Promise<void> {
    try {
      await apiFetch("/api/sideband", {
        method: "POST",
        body: JSON.stringify(payload),
      });
    } catch {
      // Silencieux — le sideband ne doit pas casser le flow d'ouverture de session.
    }
  },

  // Config ICE (STUN + TURN Cloudflare éphémère) pour le simulateur vocal WebRTC.
  // Repli STUN seul si l'endpoint échoue — ne jamais bloquer un appel.
  async getIceServers(): Promise<RTCIceServer[]> {
    try {
      const res = await apiFetch(`/api/realtime/ice-servers`, { method: "GET" });
      const json = await res.json();
      if (res.ok && Array.isArray(json.iceServers) && json.iceServers.length) {
        return json.iceServers as RTCIceServer[];
      }
    } catch {
      // repli ci-dessous
    }
    return [
      { urls: "stun:stun.l.google.com:19302" },
      { urls: "stun:stun1.l.google.com:19302" },
    ];
  },

  async saveMessages(
    sessionId: string,
    // `itemId` = item OpenAI Realtime du tour, `rank` = sa position dans la phase.
    // Les deux servent à rattacher les lignes de pilotage sans passer par une horloge.
    messages: Array<{ content: string; role: string; phase?: string; timestamp?: string; itemId?: string; rank?: number }>,
  ) {
    const res = await apiFetch(`/api/sessions/${sessionId}/messages`, {
      method: "POST",
      body: JSON.stringify({ messages }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.saveMessagesError"));
    return json.saved;
  },

  // DIAGNOSTIC (observabilité) — remonte POURQUOI une session vocale s'est terminée.
  // Best-effort : ne jette JAMAIS (un diagnostic ne doit pas impacter la simu).
  async postRealtimeDiagnostic(sessionId: string, diag: Record<string, unknown>) {
    try {
      await apiFetch(`/api/sessions/${sessionId}/realtime-diagnostic`, {
        method: "POST",
        body: JSON.stringify(diag),
      });
    } catch {
      /* best-effort */
    }
  },

  async generateFeedback(sessionId: string) {
    const res = await apiFetch(`/api/sessions/${sessionId}/feedback`, {
      method: "POST",
      body: JSON.stringify({}),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.generateFeedbackError"));
    return json.feedback;
  },

  async coachRequest(sessionId: string, formData: Record<string, unknown>) {
    const res = await apiFetch(`/api/sessions/${sessionId}/coach-request`, {
      method: "POST",
      body: JSON.stringify({ formData }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("coach.processingError"));
    // V4 — la réponse inclut recommendedSimulator + simulatorRationale (Prep Meeting).
    // Rétrocompat : si l'agent ne retourne pas ces champs, ils sont undefined.
    return {
      sections: (json.sections || []) as Array<{ title: string; content: string }>,
      recommendedSimulator: json.recommendedSimulator as string | undefined,
      simulatorRationale: json.simulatorRationale as string | undefined,
    };
  },

  async coachChat(sessionId: string, message: string) {
    const res = await apiFetch(`/api/sessions/${sessionId}/coach-chat`, {
      method: "POST",
      body: JSON.stringify({ message }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("coach.chatError"));
    return json.response as string;
  },

  // Prep Meeting → Training (2026-05-16)
  async startTrainingFromPrepMeeting(sessionId: string, simulatorId: string) {
    const res = await apiFetch(`/api/sessions/${sessionId}/start-training-from-prep-meeting`, {
      method: "POST",
      body: JSON.stringify({ simulatorId }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur création entraînement");
    return json as {
      targetId: string;
      displayName: string;
      suggestedDifficulty: "easy" | "medium" | "hard";
      scenarioInstruction: string;
      offerId: string | null;
      personaId: string | null;
    };
  },

  async requestSimulatorAccess(simulatorId: string, context?: Record<string, string>) {
    const res = await apiFetch(`/api/coach/request-simulator-access`, {
      method: "POST",
      body: JSON.stringify({ simulatorId, context: context || {} }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur demande d'accès");
    return json as { success: boolean; message: string };
  },

  // Prep Meeting — historique + brouillons (2026-05-16)
  /** `team` : préparations de l'ÉQUIPE (Pilot), en lecture seule ; `userId` réduit à une personne. */
  async listPrepMeetingHistory(opts?: { team?: boolean; userId?: string }) {
    const res = await apiFetch(opts?.team
      ? withPilotScope(`/api/coach/prep-meeting/history?team=1${opts.userId ? `&userId=${encodeURIComponent(opts.userId)}` : ""}`)
      : "/api/coach/prep-meeting/history");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement historique Coach Prepa");
    return (json.items || []) as Array<{
      id: string;
      createdAt: string;
      updatedAt: string;
      status: "draft" | "completed";
      companyName: string | null;
      contactName: string | null;
      contactRole: string | null;
      meetingObjective: string | null;
    }>;
  },

  async savePrepMeetingDraft(sessionId: string, formData: Record<string, unknown>) {
    const res = await apiFetch(`/api/sessions/${sessionId}/coach-draft`, {
      method: "POST",
      body: JSON.stringify({ formData }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur sauvegarde brouillon");
    return json as { success: boolean };
  },

  async deletePrepMeeting(sessionId: string) {
    const res = await apiFetch(`/api/coach/prep-meeting/${sessionId}`, { method: "DELETE" });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.deleteFailed")));
    return json as { success: boolean };
  },

  /** `team` : préparation d'une personne de l'équipe (Pilot), en LECTURE SEULE (décision D5). */
  async getCoachSessionDetail(sessionId: string, opts?: { team?: boolean }) {
    const res = await apiFetch(opts?.team ? withPilotScope(`/api/sessions/${sessionId}/coach-detail?team=1`) : `/api/sessions/${sessionId}/coach-detail`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("common.loadError"));
    return json as {
      sessionId: string;
      simulatorId: string | null;
      /** Préparation d'autrui : lecture seule. */
      readOnly?: boolean;
      // Refonte 2026-05-18 — statuts intermédiaires pour polling progressif
      status: "draft" | "researching" | "research_done" | "in_progress" | "completed";
      formData: Record<string, unknown>;
      sections: Array<{ title: string; content: string }> | null;
      recommendedSimulator: string | null;
      simulatorRationale: string | null;
    };
  },
};

/**
 * Ajoute `?accountId=` quand un ADM travaille sur le compte d'un client.
 *
 * Exporté pour être testé : `tests/account-api.test.ts` en gardait une COPIE,
 * qui avait divergé — elle n'encodait pas la valeur. Le test validait donc une
 * fonction que personne n'exécutait.
 */
export function accountUrl(path: string, accountId?: string): string {
  if (accountId) return `${path}${path.includes("?") ? "&" : "?"}accountId=${encodeURIComponent(accountId)}`;
  return path;
}

// Objet « Styles de personnalité » (catalogue global). GET pour tous ; écriture ADM only.
export const personalityStylesApi = {
  async list(opts: { includeInactive?: boolean } = {}) {
    const url = opts.includeInactive ? "/api/personality-styles?includeInactive=true" : "/api/personality-styles";
    const res = await apiFetch(url);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement styles de personnalité");
    return json.styles as Array<{
      id: string; code: string; category: string | null;
      labelFr: string | null; labelEn: string | null;
      descriptionFr: string | null; descriptionEn: string | null;
      isSystem: boolean; isActive: boolean; sortOrder: number;
    }>;
  },
  async create(data: { code: string; category?: string; label: string; description?: string; isActive?: boolean; sortOrder?: number; _sourceLang?: "fr" | "en" }) {
    const res = await apiFetch("/api/personality-styles", { method: "POST", body: JSON.stringify(data) });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur création style");
    return json.style;
  },
  async update(id: string, data: { category?: string; label?: string; description?: string; isActive?: boolean; sortOrder?: number; _sourceLang?: "fr" | "en" }) {
    const res = await apiFetch(`/api/personality-styles/${id}`, { method: "PUT", body: JSON.stringify(data) });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur mise à jour style");
    return json.style;
  },
  async remove(id: string) {
    const res = await apiFetch(`/api/personality-styles/${id}`, { method: "DELETE" });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur suppression style");
    return json.deleted;
  },
};

// Administration ADM « S'exercer » (DRILL) — fiches d'exercice, capsules, secteurs. ADM only.
export const drillAdminApi = {
  // ----- Fiches d'exercice -----
  async listSheets() {
    const res = await apiFetch("/api/admin/drill/sheets");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement fiches d'exercice");
    return json as { sheets: any[]; simulators: { id: string; name: string }[] };
  },
  async createSheet(data: Record<string, unknown>) {
    const res = await apiFetch("/api/admin/drill/sheets", { method: "POST", body: JSON.stringify(data) });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur création fiche");
    return json.sheet;
  },
  async importSheets(fiches: unknown[]) {
    const res = await apiFetch("/api/admin/drill/import-sheets", { method: "POST", body: JSON.stringify({ fiches }) });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur import fiches");
    return (json.results ?? []) as { label: string; status: "added" | "updated" | "error"; reason?: string }[];
  },
  async updateSheet(id: string, data: Record<string, unknown>) {
    const res = await apiFetch(`/api/admin/drill/sheets/${id}`, { method: "PUT", body: JSON.stringify(data) });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur mise à jour fiche");
    return json.sheet;
  },
  async removeSheet(id: string) {
    const res = await apiFetch(`/api/admin/drill/sheets/${id}`, { method: "DELETE" });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur suppression fiche");
    return json.deleted;
  },
  // Critères System d'un simulateur (cascade simulateur → critère dans l'éditeur de fiche).
  async simulatorCriteria(simulatorId: string) {
    const res = await apiFetch(`/api/admin/drill/simulator-criteria?simulatorId=${simulatorId}`);
    const json = await res.json();
    if (!res.ok) return [] as { key: string; labelFr: string; labelEn: string; type: string }[];
    return json.criteria as { key: string; labelFr: string; labelEn: string; type: string }[];
  },
  // Fiches méthodo du corpus Kestio pour un thème (phase) — pour le picker thème → fiche méthodo.
  async methodoSheets(phase: string) {
    const res = await apiFetch(`/api/admin/kestio-knowledge?phase=${encodeURIComponent(phase)}`);
    const json = await res.json();
    if (!res.ok) return [] as { filename: string; title: string }[];
    return (json.fiches ?? []) as { filename: string; title: string }[];
  },
  // ----- Capsules learning -----
  async listCapsules() {
    const res = await apiFetch("/api/admin/drill/capsules");
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.drillCapsulesLoad")));
    return json.capsules as any[];
  },
  async createCapsule(data: Record<string, unknown>) {
    const res = await apiFetch("/api/admin/drill/capsules", { method: "POST", body: JSON.stringify(data) });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur création capsule");
    return json.capsule;
  },
  async updateCapsule(id: string, data: Record<string, unknown>) {
    const res = await apiFetch(`/api/admin/drill/capsules/${id}`, { method: "PUT", body: JSON.stringify(data) });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur mise à jour capsule");
    return json.capsule;
  },
  async removeCapsule(id: string) {
    const res = await apiFetch(`/api/admin/drill/capsules/${id}`, { method: "DELETE" });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur suppression capsule");
    return json.deleted;
  },
  exportTraceabilityUrl(from?: string, to?: string) {
    const p = new URLSearchParams();
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    const qs = p.toString();
    return `/api/admin/drill/capsules/export${qs ? `?${qs}` : ""}`;
  },
  exportSessionsUrl(from?: string, to?: string) {
    const p = new URLSearchParams();
    if (from) p.set("from", from);
    if (to) p.set("to", to);
    const qs = p.toString();
    return `/api/admin/drill/sessions/export${qs ? `?${qs}` : ""}`;
  },
  // ----- Secteurs d'activité -----
  async listSectors() {
    const res = await apiFetch("/api/admin/drill/sectors");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement secteurs");
    return json.sectors as any[];
  },
  async createSector(data: { nameFr: string; nameEn?: string; isActive?: boolean; sortOrder?: number }) {
    const res = await apiFetch("/api/admin/drill/sectors", { method: "POST", body: JSON.stringify(data) });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur création secteur");
    return json.sector;
  },
  async updateSector(id: string, data: Record<string, unknown>) {
    const res = await apiFetch(`/api/admin/drill/sectors/${id}`, { method: "PUT", body: JSON.stringify(data) });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur mise à jour secteur");
    return json.sector;
  },
  async removeSector(id: string) {
    const res = await apiFetch(`/api/admin/drill/sectors/${id}`, { method: "DELETE" });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur suppression secteur");
    return json.deleted;
  },
};

// « S'exercer » (DRILL) — capsules learning côté utilisateur (consultation + complétion déclarative).
export const drillCapsulesApi = {
  async forCriterion(criterionKey: string, simulatorId: string) {
    const res = await apiFetch(`/api/drill/criteria/${encodeURIComponent(criterionKey)}/capsules?simulatorId=${simulatorId}`);
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.drillCapsulesLoad")));
    return json.capsules as any[];
  },
  async open(capsuleId: string, criterionKey: string) {
    const res = await apiFetch(`/api/drill/capsules/${capsuleId}/open`, { method: "POST", body: JSON.stringify({ criterionKey }) });
    return res.ok ? res.json() : null;
  },
  async heartbeat(capsuleId: string, criterionKey: string, seconds: number) {
    // best-effort (fire-and-forget côté appelant)
    const res = await apiFetch(`/api/drill/capsules/${capsuleId}/heartbeat`, { method: "POST", body: JSON.stringify({ criterionKey, seconds }) });
    return res.ok ? res.json() : null;
  },
  async complete(capsuleId: string, criterionKey: string): Promise<{ ok: boolean; reason?: string; minSeconds?: number; consultedSeconds?: number }> {
    const res = await apiFetch(`/api/drill/capsules/${capsuleId}/complete`, { method: "POST", body: JSON.stringify({ criterionKey }) });
    const json = await res.json();
    return json;
  },
};

// « S'exercer » (DRILL) — provisioning d'une session d'exercices (création + token realtime).
export const drillSessionApi = {
  async create(data: { criterionKey: string; simulatorId: string; origin: "feedback" | "history" | "progress"; sourceSessionId?: string; exerciseSheetId?: string; coachId?: string }) {
    const res = await apiFetch("/api/drill/sessions", { method: "POST", body: JSON.stringify(data) });
    const json = await res.json();
    if (!res.ok) {
      // Le serveur ne sait pas traduire (`src/i18n` est un module navigateur) : il envoie le
      // MOTIF, l'écran compose le texte — même patron que la modale de blocage des simulations.
      const err = new Error(json.message || apiErrorText(json, i18n.t("apiErrors.fallback.drillCreate"))) as Error & {
        code?: string; reason?: string; subscriptionState?: string; subscriptionEndedAt?: string | null;
        subscriptionResumesAt?: string | null; subscriptionSource?: "manual"; timeExpired?: boolean;
      };
      err.code = json.error;
      err.reason = json.reason;
      err.timeExpired = json.timeExpired;
      err.subscriptionState = json.subscriptionState;
      err.subscriptionEndedAt = json.subscriptionEndedAt;
      err.subscriptionResumesAt = json.subscriptionResumesAt;
      err.subscriptionSource = json.subscriptionSource;
      throw err;
    }
    return json as { drillSessionId: string; counted: boolean };
  },
  async realtimeToken(drillSessionId: string) {
    const res = await apiFetch(`/api/drill/sessions/${drillSessionId}/realtime-token`, { method: "POST", body: JSON.stringify({}) });
    const json = await res.json();
    if (!res.ok) throw new Error(json.message || apiErrorText(json, i18n.t("apiErrors.fallback.drillVoice")));
    return json as { agentConfig: any; audioSessionId: string; counted: boolean; sector: string | null };
  },
  async finalize(drillSessionId: string, data: { messages: { role: string; content: string; phase?: string }[]; durationSeconds?: number; roundsCompleted?: number }) {
    const res = await apiFetch(`/api/drill/sessions/${drillSessionId}/finalize`, { method: "POST", body: JSON.stringify(data) });
    return res.ok ? res.json() : null;
  },
  async history(opts?: { team?: boolean; userId?: string }) {
    // ⚠️ `?lang=` : l'endpoint sait localiser (libellé de critère), mais retombait sur `users.language`
    // — la langue du PROFIL, pas celle de l'interface. Même motif que encadrant/intents.
    // `team` : exercices de l'ÉQUIPE (Pilot, décision 2.3), `userId` réduit à une personne.
    const base = `/api/drill/history?lang=${encodeURIComponent(i18n.language || "fr")}`;
    // Sans `team` mais avec `userId` : Play › Historique du RH (« Tous les collaborateurs », D4).
    const res = await apiFetch(opts?.team
      ? withPilotScope(`${base}&team=1${opts.userId ? `&userId=${encodeURIComponent(opts.userId)}` : ""}`)
      : opts?.userId ? `${base}&userId=${encodeURIComponent(opts.userId)}` : base);
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.drillHistoryLoad")));
    return json.sessions as any[];
  },
  async detail(drillSessionId: string) {
    const res = await apiFetch(`/api/drill/sessions/${drillSessionId}/detail?lang=${encodeURIComponent(i18n.language || "fr")}`);
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.drillDetailLoad")));
    return json as { session: any; transcript: { role: string; content: string }[]; capsules: any[] };
  },
  // Couples (simulatorId, criterionKey) ayant une fiche d'exercice active → pilote l'affichage de l'icône.
  async eligibleCriteria(simulatorId?: string) {
    const res = await apiFetch(`/api/drill/eligible-criteria${simulatorId ? `?simulatorId=${simulatorId}` : ""}`);
    const json = await res.json();
    if (!res.ok) return [] as { simulatorId: string; criterionKey: string }[];
    return json.eligible as { simulatorId: string; criterionKey: string }[];
  },
};

export const accountApi = {
  async getCompany(accountId?: string) {
    const res = await apiFetch(accountUrl("/api/account/company", accountId));
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.loadCompanyError"));
    return json.company;
  },

  // ----- Trial status (cf. spec/trial-mode-with-coupon.md §4.3) -----
  async getTrialStatus() {
    const res = await apiFetch("/api/account/subscription/trial-status");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement statut essai");
    return json as {
      inTrial: boolean;
      sessionsLimit?: number;
      sessionsUsed?: number;
      trialEndsAt?: string | null;
      limitReached?: boolean;
      timeExpired?: boolean;
      requiresDecision?: boolean;
      canActivate?: boolean;
      canDecline?: boolean;
      planTier?: string;
    };
  },

  async activateTrial() {
    const res = await apiFetch("/api/account/subscription/activate-trial", { method: "POST" });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur lors de l'activation");
    return json;
  },

  async declineTrial() {
    const res = await apiFetch("/api/account/subscription/decline-trial", { method: "POST" });
    const json = await res.json();
    // Refus codé (décision Z1, 30/09/2026) : texte dans la langue de l'écran.
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("trial.declineError")));
    return json;
  },

  /** S'approprier un cas Kestio : copie éditable rattachée au compte, parenté conservée. */
  async forkTarget(targetId: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/targets/${targetId}/fork`, accountId), { method: "POST" });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.updateError"));
    return json.target;
  },

  async updateCompany(data: {
    name?: string;
    siret?: string;
    website?: string;
    industry?: string;
    address?: string;
    postalCode?: string;
    city?: string;
    country?: string;
    description?: string;
    tva?: string;
    linkedinUrl?: string;
    logoUrl?: string;
    /** Codes MV1..MV7 — cf. src/lib/sales-models.ts. Multi-valeurs. */
    salesModels?: string[];
    messageRegister?: "tu" | "vous";
  }, accountId?: string) {
    const res = await apiFetch(accountUrl("/api/account/company", accountId), {
      method: "PUT",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.updateError"));
    return json.company;
  },

  /** Où sert chaque cas : campagnes et parcours (spec/pilot-cible.md, chantier E). */
  async getTargetsUsage(accountId?: string): Promise<{ usage: Record<string, { campaigns: { id: string; title: string; status: string }[]; paths: { id: string; title: string; status: string }[] }> }> {
    const res = await apiFetch(accountId ? `/api/account/targets/usage?accountId=${accountId}` : "/api/account/targets/usage");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Usage indisponible");
    return json;
  },
  /**
   * `includeArchived` : cas DÉSACTIVÉS compris — réservé à l'écran de gestion des cas
   * (Pilot › Concevoir › Cas) ; partout ailleurs, un cas désactivé n'est plus proposé.
   */
  async getTargets(accountId?: string, opts?: { includeArchived?: boolean }) {
    const base = accountUrl("/api/account/targets", accountId);
    const url = opts?.includeArchived ? `${base}${base.includes("?") ? "&" : "?"}includeArchived=1` : base;
    const res = await apiFetch(url);
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.loadTargetsError")));
    return json.targets;
  },

  /**
   * Les cas + LE cas recommandé pour ce simulateur, avec son motif.
   *
   * Variante volontairement distincte de `getTargets` : la recommandation coûte deux requêtes
   * (sessions de l'utilisateur, profil de compétence) que la fiche compte et le wizard n'ont
   * aucune raison de payer.
   */
  async getTargetsWithRecommendation(simulatorId: string, accountId?: string) {
    const base = accountUrl("/api/account/targets", accountId);
    const res = await apiFetch(`${base}${base.includes("?") ? "&" : "?"}recommend=${encodeURIComponent(simulatorId)}`);
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.loadTargetsError")));
    return json as {
      targets: any[];
      recommandation: { targetId: string; motif: { rang: number; cle: string; critere?: string } } | null;
    };
  },

  async createTarget(data: {
    caseType?: string;
    companySize?: string;
    industry?: string;
    offerIds?: string[];
    simulatorId?: string;
    contextId?: string;
    displayName?: string;
  }, accountId?: string) {
    const res = await apiFetch(accountUrl("/api/account/targets", accountId), {
      method: "POST",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.createTargetError")));
    return json.target;
  },

  async updateTarget(id: string, data: {
    caseType?: string;
    companySize?: string;
    industry?: string;
    offerIds?: string[];
    simulatorId?: string;
    contextId?: string;
    displayName?: string | null;
  }, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/targets/${id}`, accountId), {
      method: "PUT",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.updateTargetError")));
    return json.target;
  },

  /** Désactiver (`archive`) ou réactiver un cas — Q1 du 01/10/2026. Refus codés et traduits. */
  async setTargetArchived(id: string, archive: boolean, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/targets/${id}/${archive ? "archive" : "unarchive"}`, accountId), { method: "POST" });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t(archive ? "account.archiveError" : "account.unarchiveError")));
    return json.target;
  },

  async deleteTarget(id: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/targets/${id}`, accountId), {
      method: "DELETE",
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.deleteTargetError")));
    return json.deleted;
  },

  async createPersona(targetId: string, data: {
    function: string;
    decisionMakerProfile?: string;
    painPoints?: string;
    category?: string;
    personaType?: string;
    csp?: string;
    ageRange?: string;
    gender?: string;
    purchaseCriteria?: string;
    purchasePhase?: string;
    productRelationship?: string;
    contactReason?: string;
    expressionStyle?: string;
    mainExpectation?: string;
    personalMotivations?: string;
    dailyIrritants?: string;
    typicalObjections?: string;
    neverDisclose?: string;
    personalityStyle?: string;
    personalityMode?: string;
    fixedPersonalityStyles?: string[];
    maturityLevel?: string;
    internalPressure?: string;
  }, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/targets/${targetId}/personae`, accountId), {
      method: "POST",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.createTargetError")));
    return json.persona;
  },

  async updatePersona(targetId: string, id: string, data: {
    function?: string;
    decisionMakerProfile?: string;
    painPoints?: string;
    category?: string;
    personaType?: string;
    csp?: string;
    ageRange?: string;
    gender?: string;
    purchaseCriteria?: string;
    purchasePhase?: string;
    productRelationship?: string;
    contactReason?: string;
    expressionStyle?: string;
    mainExpectation?: string;
    personalMotivations?: string;
    dailyIrritants?: string;
    typicalObjections?: string;
    neverDisclose?: string;
    personalityStyle?: string;
    personalityMode?: string;
    fixedPersonalityStyles?: string[];
    maturityLevel?: string;
    internalPressure?: string;
  }, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/targets/${targetId}/personae/${id}`, accountId), {
      method: "PUT",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.updateTargetError")));
    return json.persona;
  },

  async deletePersona(targetId: string, id: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/targets/${targetId}/personae/${id}`, accountId), {
      method: "DELETE",
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.deleteTargetError")));
    return json.deleted;
  },

  // Relation M<->N : rattache une persona EXISTANTE du compte à un cas, sans la dupliquer.
  async linkPersonaToTarget(targetId: string, personaId: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/targets/${targetId}/personae-links`, accountId), {
      method: "POST",
      body: JSON.stringify({ personaId }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.createTargetError")));
    return json;
  },

  // Délie une persona d'un cas (la persona reste au référentiel et dans ses autres cas).
  async unlinkPersonaFromTarget(targetId: string, personaId: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/targets/${targetId}/personae-links/${personaId}`, accountId), {
      method: "DELETE",
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.deleteTargetError")));
    return json.unlinked;
  },

  // Liste les cas où une persona est utilisée (« utilisée dans X cas »).
  async getPersonaUsage(personaId: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/personae/${personaId}/targets`, accountId));
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.loadTargetsError")));
    return json.targets as Array<{ id: string; displayName: string | null; companySize: string | null; industry: string | null; archivedAt: string | null }>;
  },

  async getCollaborators(accountId?: string) {
    const res = await apiFetch(accountUrl("/api/account/collaborators", accountId));
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.loadCollaboratorsError"));
    return json.collaborators;
  },

  async createCollaborator(data: {
    civility?: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
    /** Poste / intitulé de fonction. */
    position?: string;
    profileType?: string;
    managerId?: string | null;
    /** Langue d'AFFICHAGE (interface, emails). `fr` | `en`. */
    language?: string;
    /** Langue d'ENTRAÎNEMENT (scénario, simulation, débrief). `null` = suit `language`. */
    spokenLanguage?: string | null;
  }, accountId?: string) {
    const res = await apiFetch(accountUrl("/api/account/collaborators", accountId), {
      method: "POST",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.createCollaboratorError")));
    return json.collaborator;
  },

  // Envoyer UNIQUEMENT les champs modifiés (`buildCollaboratorPatch`) : le serveur ne touche
  // pas aux clés absentes. `null` / "" efface un champ facultatif (civilité, téléphone, poste).
  async updateCollaborator(id: string, data: {
    civility?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
    /** Poste / intitulé de fonction (colonne `users.position`, 20260918-1538). */
    position?: string | null;
    /** Profil LinkedIn (même colonne que celle du profil /me). */
    linkedinUrl?: string | null;
    /** Photo de profil : lien https:// ou image réduite (règle `profile-picture.ts`). */
    profilePicture?: string | null;
    managerId?: string | null;
    experienceLevel?: string;
    language?: string | null;
    spokenLanguage?: string | null;
  }, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/collaborators/${id}`, accountId), {
      method: "PUT",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.updateCollaboratorError")));
    return json.collaborator;
  },

  async getCollaboratorProgramState(id: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/collaborators/${id}/program-state`, accountId));
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement contexte");
    return json as { context: string | null; note: string | null; source: string | null; setAt: string | null; experienceLevel: string };
  },
  async setCollaboratorProgramState(id: string, data: { context: string | null; note?: string | null }, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/collaborators/${id}/program-state`, accountId), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur enregistrement contexte");
    return json as { context: string | null; note: string | null };
  },
  async getMyProgramState() {
    const res = await apiFetch("/api/users/me/program-state");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement contexte");
    return json as { context: string | null; note: string | null; source: string | null; setAt: string | null };
  },

  async deactivateCollaborator(id: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/collaborators/${id}/deactivate`, accountId), {
      method: "PUT",
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.deactivateCollaboratorError")));
    return json.collaborator;
  },

  async reactivateCollaborator(id: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/collaborators/${id}/reactivate`, accountId), {
      method: "PUT",
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.reactivateCollaboratorError")));
    return json.collaborator;
  },

  async resendMagicLink(id: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/collaborators/${id}/resend-link`, accountId), {
      method: "POST",
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.resendLinkError")));
    return json.message;
  },

  async inviteCollaborator(id: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/collaborators/${id}/invite`, accountId), {
      method: "POST",
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.resendLinkError")));
    return json.collaborator;
  },

  async updateCollaboratorProfile(id: string, profileType: string, accountId?: string, managerId?: string | null) {
    const body: any = { profileType };
    if (managerId !== undefined) body.managerId = managerId;
    const res = await apiFetch(accountUrl(`/api/account/collaborators/${id}/profile`, accountId), {
      method: "PUT",
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.updateError")));
    return json.collaborator;
  },

  async getLicences(accountId?: string) {
    // accountId : mode admin « gérer ce compte » — le catalogue inclut les licences
    // désactivées mais encore détenues par CE compte (cf. handleGetLicences).
    const res = await apiFetch(accountUrl("/api/account/licences", accountId));
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.loadLicencesError"));
    return json.licences;
  },

  async getLicenceQuotas(accountId?: string) {
    const res = await apiFetch(accountUrl("/api/account/licence-quotas", accountId));
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.loadLicencesError"));
    return json.quotas as Record<string, number>;
  },

  async getUserLicences(accountId?: string) {
    const res = await apiFetch(accountUrl("/api/account/user-licences", accountId));
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.loadAssignmentsError"));
    return json.userLicences;
  },

  /** `simulatorIds` : choix exigé pour une licence d'abonnement Starter / Focus (`lib/licence-simulators.ts`). */
  async assignLicence(userId: string, licenceId: string, accountId?: string, simulatorIds?: string[]) {
    const res = await apiFetch(accountUrl("/api/account/user-licences", accountId), {
      method: "POST",
      body: JSON.stringify({ userId, licenceId, ...(simulatorIds ? { simulatorIds } : {}) }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.assignLicenceError")));
    return json.userLicence;
  },

  /** Modifie le choix de simulateurs d'une licence Starter / Focus déjà attribuée depuis le pool. */
  async setLicenceSimulators(userId: string, licenceId: string, simulatorIds: string[], accountId?: string) {
    const res = await apiFetch(accountUrl("/api/account/user-licences/simulators", accountId), {
      method: "PUT",
      body: JSON.stringify({ userId, licenceId, simulatorIds }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.assignLicenceError")));
    return json.userLicence;
  },

  /** Simulateurs proposés au choix d'une licence d'abonnement (actifs, coachs compris). */
  async getLicenceSimulators(accountId?: string): Promise<{ id: string; name: string; type: string | null; description: string | null; descriptionFr: string | null; descriptionEn: string | null }[]> {
    const res = await apiFetch(accountUrl("/api/account/licence-simulators", accountId));
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("common.loadError")));
    return json.simulators;
  },

  async revokeLicence(userId: string, licenceId: string, accountId?: string) {
    const res = await apiFetch(accountUrl("/api/account/user-licences", accountId), {
      method: "DELETE",
      body: JSON.stringify({ userId, licenceId }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.revokeLicenceError"));
    return json.deleted;
  },

  async getSubscriptions(accountId?: string) {
    const res = await apiFetch(accountUrl("/api/account/subscription", accountId));
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement abonnements");
    return json;
  },

  async assignSeat(subscriptionId: string, userId: string, simulatorIds: string[]) {
    const res = await apiFetch("/api/account/seats", {
      method: "POST",
      body: JSON.stringify({ subscriptionId, userId, simulatorIds }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.assignSeat")));
    return json;
  },

  // Marque l'espace du compte comme configuré (fin du tunnel d'onboarding).
  async completeOnboarding(accountId?: string) {
    const res = await apiFetch(accountUrl("/api/account/onboarding-complete", accountId), { method: "POST" });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur finalisation de l'onboarding");
    return json.completed as boolean;
  },

  async unassignSeat(seatId: string) {
    const res = await apiFetch(`/api/account/seats/${seatId}`, {
      method: "DELETE",
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur désattribution licence");
    return json;
  },
  // `accountId` : compte géré (ADM/COO en « gérer ce compte ») — sinon l'onglet affichait les
  // factures du compte de l'ADM. L'impersonation (`asUserId`) est ajoutée par `apiFetch`.
  async getInvoices(accountId?: string) {
    const res = await apiFetch(accountUrl("/api/account/invoices", accountId));
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement factures");
    return json.invoices || [];
  },

  // Lien de téléchargement (simple <a href>, qui ne passe pas par `apiFetch`) : il porte lui-même
  // le compte géré et l'impersonation, comme la liste — sinon le PDF serait cherché dans un autre compte.
  getInvoicePdfUrl(invoiceId: string, accountId?: string): string {
    let url = accountUrl(`/api/account/invoices/${encodeURIComponent(invoiceId)}/pdf`, accountId);
    const asUserId = typeof window !== "undefined" ? sessionStorage.getItem(IMPERSONATE_KEY) : null;
    if (asUserId) url = `${url}${url.includes("?") ? "&" : "?"}asUserId=${encodeURIComponent(asUserId)}`;
    return url;
  },

  /** Rachat connecté sans re-saisie : carte enregistrée → débit direct ({status:"paid"}),
   *  sinon fallback page de paiement ({status:"redirect", url}). */
  async buyAgain(planCode: string, quantity: number): Promise<{ status: "paid" | "pending" | "redirect"; url?: string; subscriptionId?: string | null }> {
    const res = await apiFetch("/api/zoho/buy-again", {
      method: "POST",
      body: JSON.stringify({ planCode, quantity }),
    });
    const json = await res.json();
    // Refus codés (lot S4 : produit non achetable, quantité trop élevée) : texte dans la langue de l'écran.
    if (!res.ok) throw new Error(apiErrorText(json, json.error || "Erreur lors de l'achat"));
    return json;
  },

  // --- Account-level Personae ---
  async getAccountPersonae(accountId?: string) {
    const res = await apiFetch(accountUrl("/api/account/personae", accountId));
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement personae");
    return json.personae;
  },

  async createAccountPersona(data: {
    function: string;
    personaType?: string;
    decisionMakerProfile?: string;
    painPoints?: string;
    category?: string;
    gender?: string;
    ageRange?: string;
    csp?: string;
    purchaseCriteria?: string;
    purchasePhase?: string;
    productRelationship?: string;
    contactReason?: string;
    expressionStyle?: string;
    mainExpectation?: string;
    personalMotivations?: string;
    dailyIrritants?: string;
    typicalObjections?: string;
    neverDisclose?: string;
    personalityStyle?: string;
    personalityMode?: string;
    fixedPersonalityStyles?: string[];
    maturityLevel?: string;
    internalPressure?: string;
    targetId?: string;
  }, accountId?: string) {
    const res = await apiFetch(accountUrl("/api/account/personae", accountId), {
      method: "POST",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur creation persona");
    return json.persona;
  },

  async updateAccountPersona(id: string, data: {
    function?: string;
    personaType?: string;
    decisionMakerProfile?: string;
    painPoints?: string;
    category?: string;
    gender?: string;
    ageRange?: string;
    csp?: string;
    purchaseCriteria?: string;
    purchasePhase?: string;
    productRelationship?: string;
    contactReason?: string;
    expressionStyle?: string;
    mainExpectation?: string;
    personalMotivations?: string;
    dailyIrritants?: string;
    typicalObjections?: string;
    neverDisclose?: string;
    personalityStyle?: string;
    personalityMode?: string;
    fixedPersonalityStyles?: string[];
    maturityLevel?: string;
    internalPressure?: string;
    targetId?: string;
  }, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/personae/${id}`, accountId), {
      method: "PUT",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur mise a jour persona");
    return json.persona;
  },

  async deleteAccountPersona(id: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/personae/${id}`, accountId), {
      method: "DELETE",
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur suppression persona");
    return json.deleted;
  },

  // --- Offers ---
  async getOffers(accountId?: string) {
    const res = await apiFetch(accountUrl("/api/account/offers", accountId));
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement offres");
    return json.offers;
  },

  async createOffer(data: {
    name: string;
    description?: string;
  }, accountId?: string) {
    const res = await apiFetch(accountUrl("/api/account/offers", accountId), {
      method: "POST",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur creation offre");
    return json.offer;
  },

  async updateOffer(id: string, data: {
    name?: string;
    description?: string;
  }, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/offers/${id}`, accountId), {
      method: "PUT",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur mise a jour offre");
    return json.offer;
  },

  async deleteOffer(id: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/offers/${id}`, accountId), {
      method: "DELETE",
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur suppression offre");
    return json.deleted;
  },

  // --- Evaluation Criteria ---

  async getEvaluationCriteria(params: { simulatorId?: string } = {}, accountId?: string) {
    const qs = new URLSearchParams();
    if (params.simulatorId) qs.append("simulatorId", params.simulatorId);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    const res = await apiFetch(accountUrl(`/api/account/evaluation-criteria${suffix}`, accountId));
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.criteriaLoad")));
    return json.criteria as any[];
  },

  async createEvaluationCriterion(data: {
    simulatorId: string;
    labelFr: string;
    labelEn: string;
    descriptionFr?: string;
    descriptionEn?: string;
    type: "score" | "boolean";
    maxScore?: number;
    successThreshold?: number;
    successLabelFr?: string;
    successLabelEn?: string;
    failLabelFr?: string;
    failLabelEn?: string;
    key?: string;
  }, accountId?: string) {
    const res = await apiFetch(accountUrl("/api/account/evaluation-criteria", accountId), {
      method: "POST",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.saveFailed")));
    return json.criterion;
  },

  async updateEvaluationCriterion(id: string, data: Record<string, any>, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/evaluation-criteria/${id}`, accountId), {
      method: "PUT",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.saveFailed")));
    return json.criterion;
  },

  async deleteEvaluationCriterion(id: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/evaluation-criteria/${id}`, accountId), {
      method: "DELETE",
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.deleteFailed")));
    return json.deleted;
  },

  async toggleEvaluationCriterion(id: string, isActive: boolean, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/evaluation-criteria/${id}/toggle`, accountId), {
      method: "PUT",
      body: JSON.stringify({ isActive }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.saveFailed")));
    return json.criterion;
  },

  async toggleEvaluationCriterionProgressBar(id: string, showInProgressBar: boolean, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/evaluation-criteria/${id}/toggle-progress-bar`, accountId), {
      method: "PUT",
      body: JSON.stringify({ showInProgressBar }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.saveFailed")));
    return json.criterion;
  },

  // --- Contexts (situations business / événements déclencheurs) ---

  async getContexts(params: { simulatorId?: string } = {}, accountId?: string) {
    const qs = new URLSearchParams();
    if (params.simulatorId) qs.append("simulatorId", params.simulatorId);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    const res = await apiFetch(accountUrl(`/api/account/contexts${suffix}`, accountId));
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement contextes");
    return json.contexts as any[];
  },

  async createContext(data: Record<string, any>, accountId?: string) {
    const res = await apiFetch(accountUrl("/api/account/contexts", accountId), {
      method: "POST",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur creation contexte");
    return json.context;
  },

  // Admin (ADM/COO) — vue globale des contextes custom clients + régénération IA du brief.
  async adminListClientContexts() {
    const res = await apiFetch("/api/admin/contexts/clients");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement contextes clients");
    return json.contexts as any[];
  },

  async adminRegenerateContextBrief(id: string) {
    const res = await apiFetch(`/api/admin/contexts/${id}/regenerate-brief`, { method: "POST" });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || "Erreur régénération");
    return json;
  },

  async updateContext(id: string, data: Record<string, any>, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/contexts/${id}`, accountId), {
      method: "PUT",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur mise a jour contexte");
    return json.context;
  },

  async deleteContext(id: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/contexts/${id}`, accountId), {
      method: "DELETE",
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur suppression contexte");
    return json.deleted;
  },

  async toggleContext(id: string, isActive: boolean, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/contexts/${id}/toggle`, accountId), {
      method: "PUT",
      body: JSON.stringify({ isActive }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur toggle contexte");
    return json.context;
  },

  async translateText(text: string, targetLang: "fr" | "en", context?: string) {
    const res = await apiFetch("/api/ai/translate", {
      method: "POST",
      body: JSON.stringify({ text, targetLang, context }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur traduction");
    return json.translated as string;
  },

  async extractCriteriaFromDocument(payload: { type: "text" | "pdf" | "docx" | "xlsx" | "csv"; content: string }) {
    const res = await apiFetch("/api/ai/extract-criteria", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur lors de l'extraction");
    return json as { extracted: any[]; totalRaw: number; totalUsable: number };
  },

  async extractContextsFromDocument(payload: { type: "text" | "pdf" | "docx" | "xlsx" | "csv"; content: string }) {
    const res = await apiFetch("/api/ai/extract-contexts", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur lors de l'extraction");
    return json as { extracted: any[]; totalRaw: number; totalUsable: number };
  },
};

export const certificationPathsApi = {
  async list(accountId?: string) {
    const res = await apiFetch(accountUrl("/api/account/certification-paths", accountId));
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement parcours");
    return json.paths as any[];
  },
  async mine() {
    const res = await apiFetch("/api/account/certification-paths/mine");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement de mes parcours");
    return json.items as any[];
  },
  async available() {
    const res = await apiFetch("/api/account/certification-paths/available");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement des parcours proposés");
    return json.items as Array<{ id: string; titleFr: string; titleEn: string | null; descriptionFr: string | null; descriptionEn: string | null; origin: string; phaseCount: number }>;
  },
  async enrollSelf(pathId: string) {
    const res = await apiFetch(`/api/account/certification-paths/${pathId}/enroll`, { method: "POST" });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur lors de l'inscription");
    return json as { ok: boolean };
  },
  async postpone(pathId: string, until: string) {
    const res = await apiFetch(`/api/account/certification-paths/${pathId}/postpone`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ until }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur lors du report");
    return json as { ok: boolean; until: string };
  },
  async decline(pathId: string, reason: string) {
    const res = await apiFetch(`/api/account/certification-paths/${pathId}/decline`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ reason }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur lors du refus");
    return json as { ok: boolean };
  },
  async reactivate(pathId: string) {
    const res = await apiFetch(`/api/account/certification-paths/${pathId}/reactivate`, { method: "POST" });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur lors de la réactivation");
    return json as { ok: boolean; status: string };
  },
  async phaseProgress(phaseId: string) {
    const res = await apiFetch(`/api/account/certification-paths/phases/${phaseId}/my-progress`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement progression");
    return json as any;
  },
  async phaseCase(phaseId: string) {
    const res = await apiFetch(`/api/account/certification-paths/phases/${phaseId}/case`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement du cas de la phase");
    return json.targets as any[];
  },
  async myCertifications() {
    const res = await apiFetch("/api/account/certifications/mine");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement certifications");
    return json.certifications as Array<{ publicId: string; title: string; mention: string | null; issuedAt: string; revoked: boolean }>;
  },
  async reporting() {
    const res = await apiFetch("/api/account/certification-paths/reporting");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement reporting parcours");
    return json as {
      summary: { assigned: number; completed: number; certified: number; completionRate: number };
      byPath: Array<{ pathId: string; title: string; assigned: number; completed: number; certified: number; completionRate: number }>;
      rows: Array<{ userId: string; userName: string; pathTitle: string; pathId: string; status: string; totalPhases: number; phasesDone: number; progressPct: number; currentPhaseTitle: string | null; certified: boolean; postponedUntil: string | null; declineReason: string | null }>;
    };
  },
  async publicCertificate(publicId: string) {
    const res = await fetch(`/api/public/certificate/${publicId}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Certificat introuvable");
    return json as { publicId: string; title: string; firstName: string | null; lastName: string | null; companyName: string | null; companyLogoUrl: string | null; mention: string | null; issuedAt: string; revoked: boolean; issuer: string; openBadgeEligible: boolean };
  },
  async get(id: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/certification-paths/${id}`, accountId));
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement parcours");
    return json as { path: any; phases: any[]; canManage: boolean };
  },
  async create(data: Record<string, unknown>, accountId?: string) {
    const res = await apiFetch(accountUrl("/api/account/certification-paths", accountId), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur création parcours");
    return json.path;
  },
  async update(id: string, data: Record<string, unknown>) {
    const res = await apiFetch(`/api/account/certification-paths/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur mise à jour parcours");
    return json.path;
  },
  async remove(id: string) {
    const res = await apiFetch(`/api/account/certification-paths/${id}`, { method: "DELETE" });
    if (!res.ok) throw new Error("Erreur suppression parcours");
  },
  async createPhase(pathId: string, data: Record<string, unknown>) {
    const res = await apiFetch(`/api/account/certification-paths/${pathId}/phases`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur création phase");
    return json.phase;
  },
  async updatePhase(phaseId: string, data: Record<string, unknown>) {
    const res = await apiFetch(`/api/account/certification-phases/${phaseId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur mise à jour phase");
    return json.phase;
  },
  async deletePhase(phaseId: string) {
    const res = await apiFetch(`/api/account/certification-phases/${phaseId}`, { method: "DELETE" });
    if (!res.ok) throw new Error("Erreur suppression phase");
  },
  async getAssignableUsers(pathId: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/certification-paths/${pathId}/assignable-users`, accountId));
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement collaborateurs");
    return json.users as Array<{ id: string; firstName: string; lastName: string; email: string; profileType: string; enrolled: boolean }>;
  },
  async assign(pathId: string, userIds: string[], extra?: { startsAt?: string; dueAt?: string; campaignId?: string | null }) {
    const res = await apiFetch(`/api/account/certification-paths/${pathId}/assign`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userIds, ...(extra ?? {}) }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur assignation");
    return json.assigned as number;
  },
  /** Comme `assign`, et dit si le parcours a bien été rattaché à la campagne (`campaignId` fourni). */
  async assignDetailed(pathId: string, userIds: string[], extra?: { startsAt?: string; dueAt?: string; campaignId?: string | null }): Promise<{ assigned: number; campaignLinked?: boolean }> {
    const res = await apiFetch(`/api/account/certification-paths/${pathId}/assign`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userIds, ...(extra ?? {}) }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.code === "participant_not_trainee" ? i18n.t("participants.notTrainee") : (json.error || i18n.t("cockpit.assignError")));
    return { assigned: json.assigned as number, campaignLinked: json.campaignLinked };
  },
  async listEnrollments(pathId: string) {
    const res = await apiFetch(`/api/account/certification-paths/${pathId}/enrollments`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement inscriptions");
    return json.enrollments as any[];
  },
  async unassign(pathId: string, userId: string) {
    const res = await apiFetch(`/api/account/certification-paths/${pathId}/enrollments/${userId}`, { method: "DELETE" });
    if (!res.ok) throw new Error("Erreur désassignation");
  },
};

export type PathTemplateSummary = {
  id: string;
  code: string;
  kind: string;
  nameFr: string;
  nameEn: string;
  descriptionFr: string | null;
  descriptionEn: string | null;
  level: number;
  phaseCount: number;
};

export type PathTemplatePhase = {
  id: string;
  templateId: string;
  sequence: number;
  titleFr: string;
  titleEn: string;
  simulatorName: string;
  primaryObjective: string;
  primaryObjectiveTarget: number | null;
  minSessions: number;
  maxSessions: number;
  difficulty: string | null;
};

export const pathTemplatesApi = {
  async list() {
    const res = await apiFetch("/api/account/path-templates");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement modèles");
    return json.templates as PathTemplateSummary[];
  },
  async instantiate(templateId: string) {
    const res = await apiFetch(`/api/account/path-templates/${templateId}/instantiate`, { method: "POST" });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur création depuis le modèle");
    return json as { pathId: string };
  },

  // --- Administration Kestio (ADM) ---
  async adminList() {
    const res = await apiFetch("/api/admin/path-templates");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement modèles");
    return json.templates as Array<PathTemplateSummary & { active: boolean; orderIndex: number; phases: PathTemplatePhase[] }>;
  },
  async adminCreate(data: Record<string, unknown>) {
    const res = await apiFetch("/api/admin/path-templates", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur création du modèle");
    return json.template;
  },
  async adminUpdate(id: string, data: Record<string, unknown>) {
    const res = await apiFetch(`/api/admin/path-templates/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur mise à jour du modèle");
    return json.template;
  },
  async adminDelete(id: string) {
    const res = await apiFetch(`/api/admin/path-templates/${id}`, { method: "DELETE" });
    if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.error || "Erreur suppression"); }
  },
  async adminCreatePhase(templateId: string, data: Record<string, unknown>) {
    const res = await apiFetch(`/api/admin/path-templates/${templateId}/phases`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur création de phase");
    return json.phase as PathTemplatePhase;
  },
  async adminUpdatePhase(id: string, data: Record<string, unknown>) {
    const res = await apiFetch(`/api/admin/path-template-phases/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur mise à jour de phase");
    return json.phase as PathTemplatePhase;
  },
  async adminDeletePhase(id: string) {
    const res = await apiFetch(`/api/admin/path-template-phases/${id}`, { method: "DELETE" });
    if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.error || "Erreur suppression"); }
  },
};

export type EncadrantDuelsSummary = {
  counts: Record<string, number>;
  recent: Array<{ id: string; status: string; challenger: string | null; opponent: string | null; winner: string | null; stakeCoins: number; createdAt: string }>;
};
/** Bouton d'une alerte du cockpit — configuré en base (table `cockpit_signals`). */
export interface CockpitSignalAction {
  kind: "navigate" | "nudge" | "assign_path" | "create_challenge" | "resend_invite"
    /** Actions du plan (levier 6) : féliciter les concernés, prolonger / clôturer la campagne visée. */
    | "praise" | "extend_campaign" | "close_campaign";
  labelFr: string;
  labelEn: string;
  primary?: boolean;
  /** Cible pour `kind: "navigate"`. `bloc`/`sub` ne valent que pour `encadrant`,
   *  `tab` pour `account`. */
  target?: { page: string; bloc?: string; sub?: string; tab?: string };
}

/** Configuration d'une alerte. Les actions sont déjà filtrées par profil côté serveur. */
export interface CockpitSignalConfig {
  key: string;
  severity: "critical" | "warning" | "info";
  params: Record<string, any>;
  titleFr: string;
  titleEn: string;
  whyFr: string | null;
  whyEn: string | null;
  actions: CockpitSignalAction[];
}

export const encadrantApi = {
  async duelsSummary(): Promise<EncadrantDuelsSummary> {
    const res = await apiFetch(withPilotScope("/api/encadrant/duels-summary"));
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.duelsLoad")));
    return json as EncadrantDuelsSummary;
  },
  async cockpitSignals(): Promise<CockpitSignalConfig[]> {
    const res = await apiFetch("/api/encadrant/cockpit-signals");
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.cockpitConfigLoad")));
    return (json.signals ?? []) as CockpitSignalConfig[];
  },
  /** Félicite des collaborateurs. Même infrastructure que la relance, fenêtre
   *  bien plus courte : une reconnaissance répétée ne dérange pas. */
  async praise(userIds: string[], reason?: string, opts?: { message?: string; register?: "tu" | "vous" }): Promise<NudgeResult> {
    const res = await apiFetch(withPilotScope("/api/encadrant/praise"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userIds, reason, message: opts?.message, register: opts?.register }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.praiseFailed")));
    return json as NudgeResult;
  },
  /** Objets récemment créés d'un type donné — sert à relier une brique montée
   *  à ce qui a réellement été créé, sans instrumenter les six wizards. */
  /**
   * `mode: "reuse"` élargit au catalogue du compte, sans fenêtre de temps :
   * c'est ce qui permet de relier un parcours qui existe déjà plutôt que d'en
   * refaire un. Sans le paramètre, on ne voit que MES créations des 24 h — le
   * retour d'un montage.
   */
  async recentObjects(kind: string, mode?: "reuse"): Promise<{ id: string; label: string; createdAt: string; simulatorNames?: string[] | null }[]> {
    const q = `kind=${encodeURIComponent(kind)}${mode ? `&mode=${mode}` : ""}`;
    const res = await apiFetch(`/api/encadrant/recent-objects?${q}`);
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.recentLoad")));
    return (json.items ?? []) as { id: string; label: string; createdAt: string; simulatorNames?: string[] | null }[];
  },
  /** Corrige le cadrage — refusé une fois la campagne lancée. */
  async updateCampaign(id: string, patch: { title?: string; days?: number; memberIds?: string[]; autoLaunch?: boolean; startDate?: string | null }): Promise<void> {
    // Contexte de compte (ADM / COO placé chez un client) : sans lui, 403 (audit lot 4).
    const res = await apiFetch(withAcc(`/api/encadrant/campaigns/${id}`), {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (!res.ok) {
      const json = await res.json().catch(() => ({}));
      // Refus connus : message traduit (participants d'une campagne clôturée, décision D7).
      if (json.errorCode === "campaign_closed_members") throw new Error(i18n.t("campaign.errors.closedMembers"));
      if (json.code === "participant_not_trainee") throw new Error(i18n.t("participants.notTrainee"));
      throw new Error(apiErrorText(json, i18n.t("campaign.updateError")));
    }
  },
  /** Invite les participants à un geste de terrain (préparer, débriefer). */
  async invitePractice(
    id: string,
    payload: { memberIds: string[]; kind: "prepare" | "debrief"; message?: string; register?: "tu" | "vous" },
  ): Promise<{ sent: number; total: number }> {
    const res = await apiFetch(withAcc(`/api/encadrant/campaigns/${id}/invite`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.inviteFailed")));
    return json as { sent: number; total: number };
  },
  /** Dupliquer une campagne (décision 1.10) : la copie démarre en brouillon. */
  async duplicateCampaign(id: string): Promise<{ id: string; title: string; skippedMembers: number }> {
    const res = await apiFetch(withAcc(`/api/encadrant/campaigns/${id}/duplicate?lang=${i18n.language === "en" ? "en" : "fr"}`), { method: "POST" });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.duplicateFailed")));
    return json;
  },
  async stopCampaign(id: string, reason?: string): Promise<void> {
    const res = await apiFetch(withAcc(`/api/encadrant/campaigns/${id}/stop`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason: reason ?? "" }),
    });
    if (!res.ok) throw new Error(apiErrorText(await res.json().catch(() => ({})), i18n.t("apiErrors.fallback.closeFailed")));
  },
  /** Envoie la campagne à la corbeille — restaurable pendant la rétention. */
  async deleteCampaign(id: string): Promise<void> {
    const res = await apiFetch(withAcc(`/api/encadrant/campaigns/${id}`), { method: "DELETE" });
    if (!res.ok) throw new Error(apiErrorText(await res.json().catch(() => ({})), i18n.t("apiErrors.fallback.deleteFailed")));
  },
  /** Le manager valide son dispositif et le met en route. */
  /**
   * `challenge` rend compte de ce que le lancement a produit : qui a été
   * inscrit, qui ne l'a pas été faute de licence, si le challenge a été activé.
   * `null` quand la campagne n'en porte pas.
   */
  /** Ce que « Lancer maintenant » ferait — l'aperçu avant le geste. */
  async launchPreview(id: string): Promise<{ id: string; title: string; status: string; simulatorId: string | null; startsAt: string | null; endsAt: string | null; advancesStart: boolean; undatedEnd: string | null; launchStartsAt: string; launchEndsAt: string | null; members: number; playable: number; unplayable: { id: string; name: string; reason: "disabled" | "no_licence" }[]; missingRequired: number; rhythmPerWeek: number | null; hasChallenge: boolean; hasLicencesTab: boolean }> {
    const res = await apiFetch(withAcc(`/api/encadrant/campaigns/${id}/launch`));
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("plan.errors.launchPreviewFailed")));
    return json;
  },
  async launchCampaign(id: string): Promise<{
    status: string;
    challenge: { titre: string; inscrits: number; dejaInscrits: number; active: boolean; sansLicence: string[] } | null;
    /** Compte rendu du lancement (décision 1.4). */
    report?: { members: number; playable: number; unplayable: { id: string; name: string; reason: "disabled" | "no_licence" }[]; rhythmPerWeek: number | null };
  }> {
    const res = await apiFetch(withAcc(`/api/encadrant/campaigns/${id}/launch`), { method: "POST" });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("plan.errors.launchFailed")));
    return json;
  },
  /** Les campagnes montées par cet encadrant. */
  async campaigns(): Promise<CampaignSummary[]> {
    const res = await apiFetch(withAcc("/api/encadrant/campaigns"));
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.campaignsLoad")));
    return (json.campaigns ?? []) as CampaignSummary[];
  },
  /** Crée une campagne à l'issue du routeur. Les briques viennent du référentiel. */
  async createCampaign(input: {
    intentKey: string | null; title: string; goal?: string | null;
    days: number; memberIds: string[]; successCriteria?: unknown[];
  }): Promise<{ id: string }> {
    const res = await apiFetch(withAcc("/api/encadrant/campaigns"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.campaignCreate")));
    return json as { id: string };
  },
  async campaign(id: string): Promise<CampaignDetail> {
    const res = await apiFetch(withAcc(`/api/encadrant/campaigns/${id}`));
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.campaignLoad")));
    return json as CampaignDetail;
  },
  /** Marque une brique — c'est ce qui fait survivre la progression à la fermeture. */
  async setCampaignItem(campaignId: string, itemId: string, input: {
    status: "pending" | "done" | "skipped"; refId?: string; refLabel?: string;
  }): Promise<{ status: string; remaining: number }> {
    const res = await apiFetch(withAcc(`/api/encadrant/campaigns/${campaignId}/items/${itemId}`), {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.stepUpdate")));
    return json as { status: string; remaining: number };
  },
  /** Référentiel du routeur d'intention (26 contextes, 5 familles). */
  async intents(): Promise<IntentContext[]> {
    // La langue de l'INTERFACE, pas celle du profil.
    //
    // `handleIntentContexts` sait localiser (`?lang=`), mais retombait sur
    // `users.language` faute de paramètre : un utilisateur au profil FR qui
    // bascule l'interface en anglais gardait des situations en français —
    // « Lancer BizCoach dans l'équipe » sous un titre « What do you want to
    // achieve? ». Constaté sur dev le 04/08.
    const res = await apiFetch(`/api/encadrant/intents?lang=${encodeURIComponent(i18n.language || "fr")}`);
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.intentsLoad")));
    return (json.contexts ?? []) as IntentContext[];
  },
  /** Fiche complète d'un collaborateur — la matière d'un entretien individuel. */
  async member(userId: string): Promise<MemberProfile> {
    const res = await apiFetch(withPilotScope(`/api/encadrant/member/${userId}?lang=${encodeURIComponent(i18n.language || "fr")}`));
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.memberLoad")));
    return json as MemberProfile;
  },
  /** Toutes les mesures du cockpit, agrégées et scopées côté serveur. */
  async cockpit(days = 30): Promise<CockpitMetrics> {
    // `lang` : les noms de compétences sont localisés côté serveur (cf.
    // handleCockpitMetrics). Sans le paramètre, ils suivaient la langue du
    // PROFIL et non celle de l'interface.
    const res = await apiFetch(withPilotScope(`/api/encadrant/cockpit?days=${days}&lang=${encodeURIComponent(i18n.language || "fr")}`));
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.cockpitLoad")));
    return json as CockpitMetrics;
  },
  /** Ce que les relances du manager ont donné — la boucle qui se ferme. */
  async actionFeedback(): Promise<ActionFeedbackItem[]> {
    const res = await apiFetch("/api/encadrant/action-feedback");
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.actionFeedbackLoad")));
    return (json.items ?? []) as ActionFeedbackItem[];
  },
  /** Relance les collaborateurs d'une alerte. Le serveur écarte les hors-périmètre
   *  et ceux déjà relancés récemment — d'où le détail rendu. */
  async nudge(userIds: string[], signalKey?: string, opts?: { message?: string; register?: "tu" | "vous" }): Promise<NudgeResult> {
    const res = await apiFetch(withPilotScope("/api/encadrant/nudge"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userIds, signalKey, message: opts?.message, register: opts?.register }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.nudgeFailed")));
    return json as NudgeResult;
  },
  /** Activité du périmètre (chantier K). */
  async activity(period: string): Promise<import("./pilot-metrics.service").ActivityPayload> {
    const res = await apiFetch(withPilotScope(`/api/encadrant/activity?period=${encodeURIComponent(period)}&lang=${i18n.language}`));
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.activityLoad")));
    return json;
  },
  /** Progression du périmètre (chantier K). */
  async progression(period: string, criterion?: string | null): Promise<import("./pilot-metrics.service").ProgressionPayload> {
    const q = new URLSearchParams({ period, lang: i18n.language });
    if (criterion) q.set("criterion", criterion);
    const res = await apiFetch(withPilotScope(`/api/encadrant/progression?${q}`));
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.progressionLoad")));
    return json;
  },
  /** Note privée de 1-to-1 pour une personne. */
  async memberNote(userId: string): Promise<{ text: string; updatedAt: string | null }> {
    // Compte de contexte (ADM / COO dans un compte client) : même périmètre que le serveur.
    const res = await apiFetch(withPilotScope(`/api/encadrant/member/${userId}/note`));
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.noteLoad")));
    return json;
  },
  async saveMemberNote(userId: string, text: string): Promise<{ ok: boolean }> {
    const res = await apiFetch(withPilotScope(`/api/encadrant/member/${userId}/note`), { method: "PUT", body: JSON.stringify({ text }) });
    const json = await res.json();
    // Un refus n'est jamais un « Enregistrée » : l'écran affiche l'erreur.
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.noteSave")));
    return json;
  },
  /** Revue du lundi : les points de la semaine et l'état coché. */
  async review(): Promise<{ enabled: boolean; items: { key: string; title: string; detail: string | null; target: { bloc: string; sub: string } | null; memberIds: string[] }[]; done: string[]; dismissed: boolean; weekStart: string }> {
    const res = await apiFetch(withPilotScope(`/api/encadrant/review?lang=${encodeURIComponent(i18n.language || "fr")}`));
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.reviewLoad")));
    return json;
  },
  async saveReview(input: { done?: string[]; dismissed?: boolean }): Promise<{ ok: boolean }> {
    const res = await apiFetch("/api/encadrant/review", { method: "POST", body: JSON.stringify(input) });
    const json = await res.json().catch(() => ({}));
    // Un refus du serveur est une ERREUR : la revue ne doit jamais afficher un état non enregistré.
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("cockpit.review.saveError")));
    return json as { ok: boolean };
  },
};

// ── Plan d'entraînement (spec/plan-entrainement.md) ──────────────────────────
// Pas de table « plan » : un agrégat lu pour un périmètre et une période, et
// des gestes courts. Les types miroir de `src/lib/plan.service.ts`.

export type PlanItemType = "campaign" | "challenge" | "path" | "evaluation" | "session";

export interface PlanCampaignObjective {
  criterionKey: string;
  /** Textes du critère dans la langue du lecteur (5.2) — servis par l'API, jamais renvoyés en écriture. */
  criterionLabel?: string;
  criterionBehavior?: string | null;
  criterionHelp?: string | null;
  levelStart: number | null;
  levelTarget: number | null;
  snapshotBefore?: { at: string; level: number | null; byUser: Record<string, number>; window?: number } | null;
  snapshotAfter?: { at: string; level: number | null; byUser: Record<string, number>; window?: number } | null;
  verdict?: "achieved" | "partial" | "missed" | "unmeasured" | null;
}

export interface PlanItem {
  id: string;
  type: PlanItemType;
  title: string;
  startsAt: string;
  endsAt: string;
  undated?: boolean;
  /** Brouillon aux dates « à définir » : bandeau « À dater » du Plan. */
  needsDates?: boolean;
  /** `paused` : challenge en pause (état propre, décisions du 30/09/2026). */
  state: "draft" | "planned" | "active" | "paused" | "closed" | "done" | "missed";
  simulatorId: string | null;
  memberIds: string[];
  campaignId: string | null;
  campaign?: {
    status: string; intentKey: string | null; goal: string | null; daysLeft: number | null;
    ready: boolean; requiredPending: number; objective: PlanCampaignObjective | null;
    rhythmPerWeek: number | null; autoLaunch: boolean; outcome: string | null;
    scopeType: string | null; scopeId: string | null; createdBy: string | null;
    locked?: boolean;
    /** Le lecteur pilote-t-il la campagne ? (règle du serveur `canPilotCampaign`) */
    canPilot?: boolean;
    /** Participants que le lecteur encadre dans la campagne (grille, séances). */
    coachMemberIds?: string[];
    /** Auteur, pour « Campagne de {auteur} ». */
    authorName?: string | null;
    /** Participants qui ne peuvent pas jouer ce simulateur (sans licence, ou compte désactivé). */
    unplayable?: { id: string; reason: "disabled" | "no_licence" }[];
    objectiveNow?: number | null;
    byKestio?: boolean;
    sessionsMissed?: number;
    sessionsDone: number; sessionsPlanned: number;
  };
  session?: { userId: string; day: string; movedFrom: string | null; moveRequestedTo: string | null; sessionId: string | null; coachable?: boolean };
  path?: { pathId: string; kind: string; enrollmentIds: string[] };
  /** `canPilot` : le lecteur peut décaler ce challenge (règle serveur `canMngPilotChallenge`). */
  challenge?: { status: string; primaryObjective: string; participantCount: number; canPilot?: boolean; profileLocked?: boolean };
}

export interface PlanMember { id: string; firstName: string; lastName: string; name: string; profileType: string; managerId: string | null }
export interface PlanSimulator { id: string; name: string; icon: string | null; color: string | null }

export interface PlanPayload {
  scope: { type: "account" | "team"; id: string | null; label: string | null; editable: boolean };
  /** Internes et consultants : les comptes dans lesquels se placer. */
  accounts?: { id: string; name: string; current: boolean }[];
  from: string;
  to: string;
  /** Première action datée du périmètre (`YYYY-MM-DD`), toutes périodes confondues : borne du défilement vers le passé. */
  earliest: string | null;
  items: PlanItem[];
  members: PlanMember[];
  simulators: PlanSimulator[];
  teams: { id: string; name: string; size: number }[];
  viewer?: { profileType: string; hasLicencesTab: boolean };
}

/** Options avancées d'un challenge (création depuis le plan / une campagne). */
export interface ChallengeOptionsInput {
  description?: string | null; secondaryObjective?: string | null; difficulty?: "easy" | "medium" | "hard" | null;
  challengeType?: "mandatory" | "optional"; orangeThresholdMinutes?: number | null; redThresholdMinutes?: number | null;
  teamAssignmentMode?: "org_assigned" | "participant_choice" | null; teams?: string[];
}

export interface PlanProposedSlot {
  intentKey: string; startsAt: string; endsAt: string; days: number;
  title: string; titleEn: string | null; simulatorNames: string[]; memberIds: string[];
  /** Qui pourra jouer : dit avant de poser (licence du simulateur, compte actif). */
  simulatorId?: string | null; simulatorName?: string | null; playable?: number; unplayable?: string[];
  /** Choix de l'écran (« Lancement automatique », décision D6) ; absent = défaut de la création (oui). */
  autoLaunch?: boolean;
}

/** « Tes séances » (spec/seances.md) — une ligne de la file. */
export type SeanceSource = "campaign" | "challenge" | "path" | "evaluation" | "replay" | "assessment";
export interface SeanceTarget {
  kind: "simulator" | "case" | "retrain" | "drill" | "challenge" | "path" | "assessment";
  simulatorId: string; simulatorName?: string;
  targetId?: string | null; targetLabel?: string | null;
  sourceSessionId?: string | null; criterionKey?: string | null;
  /** Textes du critère dans la langue du lecteur (5.2) — jamais stockés. */
  criterionLabel?: string | null; criterionBehavior?: string | null; criterionHelp?: string | null;
  exerciseSheetId?: string | null; challengeId?: string | null; pathId?: string | null; pathPhaseId?: string | null;
}
export interface Seance {
  id: string; source: SeanceSource; status: string; day: string | null; position: number;
  title: string; why: string; target: SeanceTarget; rewardCoins: number; campaignId: string | null;
  movedFrom: string | null; moveRequestedTo: string | null;
}
/** Bilan de campagne — vue manager (tous les membres) ou vue participant (`me` + un seul membre). */
export interface CampaignReport {
  id: string; title: string; goal: string | null; simulatorName: string | null;
  startsAt: string | null; endsAt: string | null; closedAt: string | null;
  status: string; outcome: string | null; outcomeNote: string | null; verdict: string | null; managerName: string | null;
  objective: { criterionKey: string; criterionLabel: string; criterionBehavior?: string | null; criterionHelp?: string | null; levelStart: number | null; levelEnd: number | null; levelTarget: number | null; window: number | null } | null;
  participation: { rate: number; active: number; total: number };
  sessions: { done: number; planned: number; missed: number };
  teamCurve: { week: string; level: number; n?: number }[];
  members: { id: string; name: string; sessionsDone: number; sessionsPlanned: number; played: number; levelStart: number | null; levelEnd: number | null; delta: number | null }[];
  me?: { rank: number | null; of: number; curve: { week: string; level: number }[] };
}

export interface MySeancesPayload {
  queue: Seance[];
  /** Décision 3.7 : au moins un simulateur jouable (licence nominative ou pack du compte). */
  hasAccess?: boolean;
  /** Z4 (30/09/2026) : l'accès du compte aux simulateurs est terminé — un seul message à l'écran. */
  accessEnded?: boolean;
  /** Dernière campagne terminée (3 semaines) : bilan personnel à célébrer. */
  closedCampaign?: CampaignReport | null;
  recentDone: Seance[];
  catchUps: { campaignId: string; count: number; oldestDay: string; title: string; seanceId: string | null; simulatorId: string | null; targetId: string | null }[];
  counters: { sinceLastVisit: number; last30Days: number; queueDone: number; queueTotal: number; daysSinceLast: number | null };
  streak: { current: number; longest: number; justFull: boolean };
  /** La campagne en cours de la personne : ce qu'elle y a fait, sa courbe sur le critère visé. */
  myCampaign?: { id: string; title: string; done: number; total: number; daysLeft: number | null; criterionLabel: string | null; criterionBehavior?: string | null; criterionHelp?: string | null; levelStart: number | null; levelNow: number | null; levelTarget: number | null; curve: { week: string; level: number }[] } | null;
}
/** @deprecated remplacé par `Seance` — conservé pour les imports existants. */
export type MyPlannedSession = Seance;

/**
 * Contexte de compte du plan (internes / consultants) : posé par l'écran, ajouté
 * à chaque appel encadrant du plan. Les appels côté bénéficiaire (`mine`,
 * `moveMine`) n'en ont pas besoin.
 */
function withAcc(url: string): string { return withPilotScope(url); }

async function planJson<T>(res: Response, fallback: string): Promise<T> {
  const json = await res.json().catch(() => ({}));
  // Code d'erreur connu (ex. RH / GES refusé comme participant) : message traduit, comme les routes challenge.
  if (!res.ok) throw apiError(json as { error?: string; code?: string }, fallback);
  return json as T;
}

export interface PlanWeekCampaign {
  id: string; title: string; state: string; simulatorName: string | null;
  startsAt: string; endsAt: string; daysLeft: number | null; elapsedRatio: number; participationRate: number;
  participantIds: string[]; absentIds: string[]; sessionsDone: number; sessionsPlanned: number;
  objective: { criterionLabel: string; criterionBehavior?: string | null; criterionHelp?: string | null; levelStart: number | null; levelNow: number | null; levelTarget: number | null; reached: boolean } | null;
  /** Le lecteur pilote la campagne : lui seul la prolonge ou la clôt (règle serveur). */
  canPilot?: boolean;
}
/** Par participant, par jour ouvré (1 = lundi … 5 = vendredi) : les autres campagnes déjà prévues ce jour-là. */
export type RhythmOverlaps = Record<string, Record<number, { id: string; title: string }[]>>;

export interface LicenceStatusPayload {
  members: { id: string; status: "ok" | "no_licence" | "disabled"; canAssign: boolean }[];
  /** Licences du simulateur avec des places libres dans le pool du compte. */
  pool: { licenceId: string; name: string; available: number }[];
  /** ORG / ADM : « Acheter des licences » dans un nouvel onglet quand le pool est vide. */
  canBuy: boolean;
}

export interface RhythmGridPayload {
  perWeek: number;
  /** Ligne « Tous les participants » : les jours de toute l'équipe ([] = aucune session). */
  campaignWeekdays: number[];
  /** Le lecteur pilote la campagne : lui seul change son rythme. */
  canSetRhythm: boolean;
  members: { id: string; name: string; firstName: string; weekdays: number[]; personalized: boolean; editable: boolean; playable: boolean }[];
  overlaps: RhythmOverlaps;
}

export interface PlanWeekPayload {
  weekStart: string;
  campaigns: PlanWeekCampaign[];
  notYet: { id: string; name: string; plannedThisWeek: number }[];
  progressed: { id: string; name: string; delta: number; skill: string }[];
  weekMissed: { id: string; name: string }[];
  nextCampaignInDays: number | null;
  /** Campagnes pilotées par le lecteur, parties en retard ou pas parties (alerte du cockpit). */
  notLaunched?: { id: string; title: string; reason: "not_ready" | "nobody_playable" | "error" | "manual" }[];
}

export const planApi = {
  /** Se placer dans un compte (ADM / COO / CSL). `null` = son propre compte. */
  /** Le périmètre vient de l'en-tête de Pilot (`setPilotScope`) ; conservé pour compatibilité. */
  setAccount(_id: string | null) { /* no-op */ },
  async scopes(): Promise<{ accounts: { id: string; name: string; current: boolean }[]; teams: { id: string; name: string; size: number }[]; current: { accountId: string | null; scope: string | null }; label: string | null; people: number; canPickAccount: boolean; messageRegister?: "tu" | "vous" }> {
    return planJson(await apiFetch(withAcc("/api/plan/scopes")), i18n.t("plan.errors.scopeUnavailable"));
  },
  get accountId() { return getPilotScope().account; },
  /** « Cette semaine dans le plan » : campagnes en cours, absents, progressions, matière des signaux. */
  async week(scope?: string | null): Promise<PlanWeekPayload> {
    const q = new URLSearchParams({ lang: i18n.language });
    if (scope) q.set("scope", scope);
    return planJson(await apiFetch(withAcc(`/api/plan/week?${q}`)), i18n.t("plan.errors.weekLoadFailed"));
  },
  async get(params: { scope?: string | null; from: string; to: string }): Promise<PlanPayload> {
    const q = new URLSearchParams({ from: params.from, to: params.to, lang: i18n.language });
    if (params.scope) q.set("scope", params.scope);
    return planJson(await apiFetch(withAcc(`/api/plan?${q.toString()}`)), i18n.t("plan.errors.planLoadFailed"));
  },
  async quick(input: {
    type: "campaign" | "sessions"; title?: string; startsAt: string; endsAt: string;
    simulatorId?: string | null; memberIds: string[]; campaignId?: string | null; intentKey?: string | null;
    perWeek?: number; scopeType?: "account" | "team"; scopeId?: string | null;
    /** « Lancement automatique » (décision 1.2) : coché par défaut. */
    autoLaunch?: boolean;
    /** Grille de rythme (décision 1.1) : jours de chaque participant (1 = lundi … 5 = vendredi). */
    memberWeekdays?: Record<string, number[]>;
    /** Ligne « Tous les participants » : les jours de toute l'équipe. */
    weekdays?: number[];
  }): Promise<{ id?: string; created?: number; skipped?: { id: string; name: string; reason: "disabled" | "no_licence" }[] }> {
    return planJson(await apiFetch(withAcc(`/api/plan/quick?lang=${i18n.language}`), { method: "POST", body: JSON.stringify(input) }), i18n.t("plan.errors.createFailed"));
  },
  async updateDates(type: PlanItemType, id: string, input: { startsAt: string; endsAt: string; moveChildren?: boolean; memberIds?: string[] }): Promise<{ ok: boolean; clamped?: boolean; parentExtendedTo?: string }> {
    return planJson(await apiFetch(withAcc(`/api/plan/${type}/${id}/dates`), { method: "PATCH", body: JSON.stringify(input) }), i18n.t("plan.errors.updateFailed"));
  },
  /** Dater le parcours d'évaluation de ces personnes (inscrites au passage si besoin). */
  async scheduleEvaluation(input: { memberIds: string[]; startsAt: string; endsAt: string }): Promise<{ ok: boolean; scheduled: number; pathId: string | null }> {
    return planJson(await apiFetch(withAcc(`/api/plan/evaluation/schedule`), { method: "POST", body: JSON.stringify(input) }), i18n.t("plan.errors.scheduleFailed"));
  },
  async setObjective(campaignId: string, input: { criterionKey: string; levelTarget: number | null }): Promise<{ ok: boolean; objective: PlanCampaignObjective }> {
    return planJson(await apiFetch(withAcc(`/api/plan/campaigns/${campaignId}/objective`), { method: "POST", body: JSON.stringify(input) }), i18n.t("plan.errors.objectiveNotSaved"));
  },
  async propose(input: { intentKeys: string[]; from: string; to: string; scope?: string | null }): Promise<{ slots: PlanProposedSlot[]; skipped: number; hasLicencesTab?: boolean }> {
    return planJson(await apiFetch(withAcc(`/api/plan/propose`), { method: "POST", body: JSON.stringify(input) }), i18n.t("plan.errors.proposeFailed"));
  },
  async applyProposal(input: { slots: { intentKey: string; startsAt: string; endsAt: string; title?: string; memberIds?: string[]; autoLaunch?: boolean }[]; scope?: string | null }): Promise<{ ids: string[] }> {
    return planJson(await apiFetch(withAcc(`/api/plan/propose/apply?lang=${i18n.language}`), { method: "POST", body: JSON.stringify(input) }), i18n.t("plan.errors.placeFailed"));
  },
  async deleteSession(id: string): Promise<{ ok: boolean }> {
    return planJson(await apiFetch(withAcc(`/api/plan/sessions/${id}`), { method: "DELETE" }), i18n.t("plan.errors.deleteFailed"));
  },
  /** Courbe d'équipe d'une campagne sur son critère visé. */
  async campaignCurve(campaignId: string): Promise<{ criterionKey: string | null; points: { week: string; level: number; n?: number }[]; teams?: { managerId: string; name: string; size: number; points: { week: string; level: number }[]; delta: number | null }[] }> {
    return planJson(await apiFetch(withAcc(`/api/plan/campaigns/${campaignId}/curve`)), i18n.t("plan.errors.curveUnavailable"));
  },
  /** Grille de rythme d'une campagne (décisions 1.1 et 1.11). */
  async rhythmGrid(campaignId: string): Promise<RhythmGridPayload> {
    return planJson(await apiFetch(withAcc(`/api/plan/campaigns/${campaignId}/rhythm`)), i18n.t("plan.errors.gridUnavailable"));
  },
  /** Enregistre la grille : rythme (pilote) et/ou jours des participants (qui les encadre), puis repose les sessions à venir. */
  async saveRhythmGrid(campaignId: string, input: { perWeek?: number; campaignWeekdays?: number[]; memberWeekdays: Record<string, number[] | null> }): Promise<{ created: number; perWeek: number }> {
    return planJson(await apiFetch(withAcc(`/api/plan/campaigns/${campaignId}/rhythm-grid`), { method: "POST", body: JSON.stringify(input) }), i18n.t("plan.errors.daysNotSaved"));
  },
  /** Licence du simulateur pour chaque participant, places libres du pool, gestes permis (décision 1.5). */
  async licenceStatus(input: { simulatorId: string; memberIds: string[] }): Promise<LicenceStatusPayload> {
    return planJson(await apiFetch(withAcc(`/api/plan/licence-status`), { method: "POST", body: JSON.stringify(input) }), i18n.t("plan.errors.licencesUnavailable"));
  },
  /** « Demander une licence » aux organisateurs, avant ou après la création de la campagne. */
  async requestLicencesForSelection(input: { simulatorId: string; memberIds: string[]; campaignId?: string }): Promise<{ sent: number; names: string[]; already?: boolean }> {
    return planJson(await apiFetch(withAcc(`/api/plan/licence-request`), { method: "POST", body: JSON.stringify(input) }), i18n.t("plan.errors.requestNotSent"));
  },
  /** Attribue une licence du pool — même endpoint (et mêmes droits) que Mon compte › Licences. */
  async assignLicence(userId: string, licenceId: string, simulatorIds?: string[]): Promise<void> {
    await planJson(await apiFetch(withAcc(`/api/account/user-licences`), { method: "POST", body: JSON.stringify({ userId, licenceId, ...(simulatorIds ? { simulatorIds } : {}) }) }), i18n.t("plan.errors.licenceNotAssigned"));
  },
  /** Jours déjà pris par d'autres campagnes, pour une campagne en création. */
  async rhythmOverlaps(input: { memberIds: string[]; startsAt: string; endsAt: string; excludeCampaignId?: string | null }): Promise<{ overlaps: RhythmOverlaps }> {
    return planJson(await apiFetch(withAcc(`/api/plan/rhythm-overlaps`), { method: "POST", body: JSON.stringify(input) }), i18n.t("plan.errors.overlapsUnavailable"));
  },
  /** « Préparer et lancer » : monte ce qui se déduit, lance si plus rien ne manque. */
  async report(campaignId: string): Promise<CampaignReport> {
    return planJson(await apiFetch(withAcc(`/api/plan/campaigns/${campaignId}/report?lang=${i18n.language}`)), i18n.t("plan.errors.reportUnavailable"));
  },
  reportPdfUrl(campaignId: string): string { return withAcc(`/api/plan/campaigns/${campaignId}/report.pdf?lang=${i18n.language}`); },
  /** Destinataires possibles du bilan (règle unique du serveur, décision D8). */
  async reportRecipients(campaignId: string): Promise<{ id: string; name: string; email: string }[]> {
    const r = await planJson(await apiFetch(withAcc(`/api/plan/campaigns/${campaignId}/report/recipients`)), i18n.t("common.loadError")) as { recipients: { id: string; name: string; email: string }[] };
    return r.recipients ?? [];
  },
  async sendReport(campaignId: string, input: { to: string[]; message?: string }): Promise<{ ok: boolean; sent: number; total: number }> {
    return planJson(await apiFetch(withAcc(`/api/plan/campaigns/${campaignId}/report/send?lang=${i18n.language}`), { method: "POST", body: JSON.stringify(input) }), i18n.t("plan.errors.sendFailed"));
  },
  /** Estimation de crédits (avertissement) : ce que la pose consommerait face au disponible. */
  async quota(input: { memberIds: string[]; simulatorId: string | null; sessions: number }): Promise<{ sessions: number; available: number | null; cycleEnd: string | null; short: boolean }> {
    return planJson(await apiFetch(withAcc("/api/plan/quota"), { method: "POST", body: JSON.stringify(input) }), i18n.t("plan.errors.estimateFailed"));
  },
  /** `launch` : true seulement après confirmation du lancement (le serveur ne lance jamais par défaut). */
  async prepare(campaignId: string, launch = false): Promise<{ done: { kind: string; label: string; created: boolean }[]; remaining: string[]; ready: boolean; launched: boolean; sansLicence: string[] }> {
    return planJson(await apiFetch(withAcc(`/api/plan/campaigns/${campaignId}/prepare`), { method: "POST", body: JSON.stringify({ launch }) }), i18n.t("plan.errors.prepareFailed"));
  },
  /** Le challenge intra pré-rempli d'une campagne (brouillon, activé au lancement). */
  async createStandaloneChallenge(input: { title: string; simulatorId: string; primaryObjective?: string | null; startDate?: string; endDate?: string; memberIds: string[]; minSessions?: number; maxSessions?: number } & ChallengeOptionsInput): Promise<{ id: string; title: string; primaryObjective: string; participants: number; sansLicence: string[] }> {
    return planJson(await apiFetch(withPilotScope("/api/plan/challenges"), { method: "POST", body: JSON.stringify(input) }), i18n.t("plan.errors.challengeCreateFailed"));
  },
  async createChallenge(campaignId: string, input: { title?: string; simulatorId?: string | null; primaryObjective?: string | null; startDate?: string; endDate?: string; memberIds?: string[]; minSessions?: number; maxSessions?: number } & ChallengeOptionsInput): Promise<{ id: string; title: string; primaryObjective: string; participants: number }> {
    return planJson(await apiFetch(withAcc(`/api/plan/campaigns/${campaignId}/challenge`), { method: "POST", body: JSON.stringify(input) }), i18n.t("plan.errors.challengeCreateFailed"));
  },
  /** La file « Tes séances » — recomposée à chaque appel côté serveur. */
  async mine(): Promise<MySeancesPayload> {
    return planJson(await apiFetch(`/api/users/me/planned-sessions?lang=${i18n.language}`), i18n.t("plan.errors.sessionsLoadFailed"));
  },
  /** Le cas d'une séance, avec ses personae — garanti même hors de la liste des cas. */
  async seanceCase(id: string): Promise<any[]> {
    const j = await planJson<{ targets: any[] }>(await apiFetch(`/api/plan/seances/${id}/case`), i18n.t("plan.errors.caseNotFound"));
    return j.targets ?? [];
  },
  /** Décisions 3.5 / 3.5 bis : `requestedTo` = la personne qui reçoit la demande (son nom est affiché). */
  async moveMine(id: string, day: string): Promise<{ moved: boolean; requested: boolean; requestedTo?: { id: string; name: string } }> {
    return planJson(await apiFetch(`/api/users/me/planned-sessions/${id}`, { method: "PATCH", body: JSON.stringify({ day }) }), i18n.t("plan.errors.moveFailed"));
  },
};


/** Une compétence mesurée, telle que servie par `/api/encadrant/cockpit`. */
export interface CockpitSkill {
  key: string;
  /** Libellé du critère, dans la langue du lecteur (langue d'entraînement comprise). */
  label: string;
  /** Comportement attendu (même langue) ; `null` pour un critère qui n'en porte pas (5.1). */
  behavior?: string | null;
  /** Définition, affichée au survol et au toucher. */
  help?: string | null;
  level: number;
  /** `score` = note sur 5 · `rate` = taux de réussite en % (critère booléen). */
  unit: "score" | "rate";
  samples: number;
  userCount: number;
  /** Écart avec la période précédente. */
  delta: number | null;
}

export interface CockpitMember {
  id: string;
  name: string;
  managerId?: string | null;
  /** Profil (décision 3.6 : RH et GES ne sont pas proposés comme participants). */
  profileType?: string | null;
  /** Niveau sur 5, `null` si aucune mesure — inconnu, pas faible. */
  level: number | null;
  samples: number;
  sessions: number;
  previousSessions: number;
  daysSinceLast: number | null;
  engagement: number;
  daysSinceJoined: number | null;
}

/** Une équipe du périmètre (manager + ses gens), pour les périmètres multi-équipes. */
export interface CockpitTeam {
  managerId: string;
  name: string;
  size: number;
  active: number;
  sessions: number;
  level: number | null;
}

export interface CockpitMetrics {
  days: number;
  members: CockpitMember[];
  skills: CockpitSkill[];
  teamLevel: number | null;
  teams?: CockpitTeam[];
  measuredCount: number;
  scale: number;
}

/** Résumé d'une campagne, pour la liste. */
export interface CampaignSummary {
  id: string;
  title: string;
  goal: string | null;
  status: "draft" | "active" | "closed";
  intentKey: string | null;
  startsAt: string | null;
  endsAt: string | null;
  memberCount: number;
  itemsDone: number;
  itemsTotal: number;
  outcome: "achieved" | "partial" | "missed" | "unmeasured" | null;
  daysLeft: number | null;
}

export interface CampaignItem {
  id: string;
  kind: string;
  required: boolean;
  status: "pending" | "done" | "skipped";
  refId: string | null;
  refLabel: string | null;
}

export interface CampaignDetail {
  campaign: {
    id: string; title: string; goal: string | null;
    status: "draft" | "active" | "closed"; intentKey: string | null;
    startsAt: string | null; endsAt: string | null;
    /** Dates « à définir » : durée prévue (jours), pour proposer la fin quand le début sera choisi. */
    plannedDays?: number | null;
    /** Toujours un objet : l'objectif chiffré (`criterionKey`, niveaux…) ou `{}`. */
    successCriteria: Record<string, unknown>;
    outcome: string | null; outcomeNote: string | null; closedAt: string | null;
    daysLeft: number | null;
    /** Plan d'entraînement : simulateur, rythme, objectif chiffré, lancement automatique. */
    simulatorId?: string | null;
    rhythmPerWeek?: number | null;
    autoLaunch?: boolean;
    objective?: PlanCampaignObjective | null;
    /** Forme du tunnel héritée de la situation. */
    campaignType: string;
    /** Simulateurs mis en avant dans les wizards de cas et de challenge. */
    simulatorNames: string[];
  };
  /** Présent quand la campagne porte une brique « inviter l'équipe ». */
  practice: {
    itemId: string;
    kind: "prepare" | "debrief";
    /** Gestes réalisés par participant depuis le lancement. */
    counts: Record<string, number>;
  } | null;
  items: CampaignItem[];
  members: { id: string; name: string; profileType?: string | null }[];
  /** Ce que le lecteur peut faire — décidé par le serveur (piloter / encadrer). */
  viewer?: { canPilot: boolean; canDelete: boolean; coachMemberIds: string[] };
}

/** Un contexte du routeur d'intention, tel que servi par l'API. */
export interface IntentContext {
  key: string;
  family: string;
  label: string;
  goal: string;
  defaultDays: number;
  suggestedTitle: string;
  steps: { kind: string; required: boolean }[];
  /** Règle de pré-sélection choisie en admin. `null` = toute l'équipe. */
  audienceRule: string | null;
  /** Seuil en jours de cette règle. `null` = défaut de la règle. */
  audienceThreshold: number | null;
  /** `challenge` (mono-simulateur) | `path` (multi) | `activation`. */
  campaignType: string;
  /** Simulateurs mis en avant pour cette situation. Vide = tous. */
  simulatorNames: string[];
}

export interface MemberProfile {
  member: {
    id: string;
    name: string;
    email: string | null;
    profileType: string;
    status: string;
    daysSinceJoined: number | null;
  };
  level: number | null;
  samples: number;
  sessions: number;
  previousSessions: number;
  daysSinceLast: number | null;
  engagement: number;
  skills: CockpitSkill[];
  recentSessions: Array<{
    id: string;
    createdAt: string;
    durationInMinutes: number | null;
    simulatorName: string | null;
    totalScore: number | null;
  }>;
  nudges: Array<{ kind: string; createdAt: string; signalKey: string | null }>;
  scale: number;
  /** Courbes par phase sur 12 semaines (photos hebdo). */
  curves?: Record<string, { week: string; level: number }[]>;
  /** « Ses séances » : la file du moment, les rattrapages, les faites sur 30 jours. */
  seances?: { queue: { id: string; source: SeanceSource; title: string; why: string; day: string | null }[]; catchUps: { title: string; count: number }[]; done30: number; accessEnded?: boolean; canResubscribe?: boolean } | null;
}

export interface ActionFeedbackItem {
  userId: string;
  userName: string;
  signalKey: string | null;
  sentAt: string;
  daysAgo: number;
  sessionsSince: number;
  outcome: "worked" | "no_effect";
}

export interface NudgeResult {
  sent: number;
  /** Déjà relancés dans la fenêtre (tous émetteurs confondus). */
  skippedCooldown: number;
  /** Hors du périmètre de l'encadrant. */
  skippedOutOfScope: number;
  /** Fenêtre anti-doublon d'une RELANCE, en jours. */
  cooldownDays: number;
  /** Fenêtre anti-doublon d'une FÉLICITATION, en heures — bien plus courte :
   *  une relance répétée harcèle, une félicitation répétée fait plaisir. */
  cooldownHours?: number;
}

export type FirstStepsState = { s1: boolean; s3: boolean; s4: boolean; evaluationPathId: string | null };
export const firstStepsApi = {
  async getMine(): Promise<FirstStepsState> {
    const res = await apiFetch("/api/users/me/first-steps");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement onboarding");
    return json as FirstStepsState;
  },
};

/**
 * Erreur d'une route challenge : garde le `code` renvoyé par le serveur pour que l'écran puisse
 * afficher un message traduit (`challengeErrorMessage`, `src/lib/challenge-participants.ts`).
 */
/**
 * Texte d'une réponse d'erreur d'API dans la langue de l'ÉCRAN (lot 9) : code connu → clé i18n
 * (`apiErrors.<code>`, codes des challenges, codes historiques) avec ses `params` ; sinon le message
 * du serveur ; sinon le repli fourni (déjà traduit). Mécanisme unique : `src/lib/api-errors.ts`.
 */
export function apiErrorText(json: { error?: string; code?: string; errorCode?: string; params?: Record<string, string | number> } | null | undefined, fallback: string): string {
  const code = json?.code ?? json?.errorCode;
  const key = code ? (CHALLENGE_ERROR_KEYS[code] ?? apiErrorI18nKey(code)) : undefined;
  if (key) return String(i18n.t(key, json?.params ?? {}));
  return json?.error || fallback;
}

/** Même chose, sous forme d'`Error` qui garde le `code` (l'écran peut réagir à un refus précis). */
export function apiError(json: { error?: string; code?: string; errorCode?: string; params?: Record<string, string | number> } | null | undefined, fallback: string): Error & { code?: string } {
  const err: Error & { code?: string } = new Error(apiErrorText(json, fallback));
  const code = json?.code ?? json?.errorCode;
  if (code) err.code = code;
  return err;
}
const challengeApiError = apiError;

export const challengesApi = {
  async getLeaderboard(id: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/challenges/${id}/leaderboard`, accountId));
    const json = await res.json();
    if (!res.ok) throw challengeApiError(json, "Erreur chargement leaderboard");
    return json as { challenge: any; teams: any[]; leaderboard: any[] };
  },

  async getChallenges(accountId?: string) {
    const res = await apiFetch(accountUrl("/api/account/challenges", accountId));
    const json = await res.json();
    if (!res.ok) throw challengeApiError(json, "Erreur chargement challenges");
    return json.challenges;
  },

  async getChallenge(id: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/challenges/${id}`, accountId));
    const json = await res.json();
    if (!res.ok) throw challengeApiError(json, "Erreur chargement challenge");
    return json.challenge;
  },

  async myProgress(id: string) {
    const res = await apiFetch(`/api/account/challenges/${id}/my-progress`);
    const json = await res.json();
    if (!res.ok) throw challengeApiError(json, "Erreur chargement progression challenge");
    return json as { minSessions: number; maxSessions: number; primaryObjective: string; secondaryObjective: string | null; sessionsCount: number };
  },

  // Banner: only new challenges not yet joined (for announcements)
  async getMyChallenges() {
    const res = await apiFetch("/api/account/challenges/mine");
    const json = await res.json();
    if (!res.ok) throw challengeApiError(json, "Erreur chargement mes challenges");
    return json.challenges as Array<{ id: string; title: string; endDate?: string; simulatorName?: string; simulatorIcon?: string; simulatorColor?: string; enrolled: boolean; challengeType?: string; primaryObjective?: string; maxSessions?: number }>;
  },

  async getEligibleUsers(simulatorId: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/challenges/eligible-users?simulatorId=${simulatorId}`, accountId));
    const json = await res.json();
    if (!res.ok) throw challengeApiError(json, "Erreur chargement utilisateurs éligibles");
    return json.users as Array<{ id: string; firstName: string; lastName: string; email: string; profileType: string }>;
  },

  // Challenges page: all active challenges (enrolled + available)
  async listMyChallenges() {
    const res = await apiFetch("/api/account/challenges/list");
    const json = await res.json();
    if (!res.ok) throw challengeApiError(json, "Erreur chargement challenges");
    return json.challenges as Array<{ id: string; title: string; startDate?: string; endDate?: string; simulatorName?: string; simulatorIcon?: string; simulatorColor?: string; enrolled: boolean; challengeType?: string; primaryObjective?: string; minSessions?: number; maxSessions?: number }>;
  },

  async joinChallenge(id: string, teamId?: string) {
    const res = await apiFetch(`/api/account/challenges/${id}/join`, {
      method: "POST",
      ...(teamId ? { body: JSON.stringify({ teamId }) } : {}),
    });
    const json = await res.json();
    if (!res.ok) throw challengeApiError(json, "Erreur inscription challenge");
    return json.joined;
  },

  async leaveChallenge(id: string) {
    const res = await apiFetch(`/api/account/challenges/${id}/leave`, { method: "POST" });
    const json = await res.json();
    if (!res.ok) throw challengeApiError(json, "Erreur désinscription challenge");
    return json.left;
  },

  async createTeam(challengeId: string, data: { name: string; color?: string }, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/challenges/${challengeId}/teams`, accountId), {
      method: "POST",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw challengeApiError(json, "Erreur création équipe");
    return json.team;
  },

  async updateTeam(challengeId: string, teamId: string, data: { name?: string; color?: string }, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/challenges/${challengeId}/teams/${teamId}`, accountId), {
      method: "PUT",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw challengeApiError(json, "Erreur mise à jour équipe");
    return json.team;
  },

  async deleteTeam(challengeId: string, teamId: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/challenges/${challengeId}/teams/${teamId}`, accountId), {
      method: "DELETE",
    });
    const json = await res.json();
    if (!res.ok) throw challengeApiError(json, "Erreur suppression équipe");
    return json.deleted;
  },

  async assignTeams(challengeId: string, assignments: Array<{ participantId: string; teamId: string | null }>, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/challenges/${challengeId}/assign-teams`, accountId), {
      method: "PUT",
      body: JSON.stringify({ assignments }),
    });
    const json = await res.json();
    if (!res.ok) throw challengeApiError(json, "Erreur attribution équipes");
    return json.updated;
  },

  async addParticipants(challengeId: string, userIds: string[], teamId?: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/challenges/${challengeId}/participants`, accountId), {
      method: "POST",
      body: JSON.stringify({ userIds, teamId }),
    });
    const json = await res.json();
    if (!res.ok) throw challengeApiError(json, "Erreur ajout participants");
    return json.added as number;
  },

  async removeParticipant(challengeId: string, participantId: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/challenges/${challengeId}/participants/${participantId}`, accountId), {
      method: "DELETE",
    });
    const json = await res.json();
    if (!res.ok) throw challengeApiError(json, "Erreur retrait participant");
    return json.removed;
  },

  async createChallenge(data: {
    title: string;
    description?: string;
    startDate?: string;
    endDate?: string;
    simulatorId: string;
    minSessions?: number;
    maxSessions?: number;
    orangeThresholdMinutes?: number;
    redThresholdMinutes?: number;
    primaryObjective: string;
    secondaryObjective?: string;
    challengeType?: string;
    teamAssignmentMode?: string;
    teams?: Array<{ name: string; color?: string }>;
  }, accountId?: string) {
    const res = await apiFetch(accountUrl("/api/account/challenges", accountId), {
      method: "POST",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw challengeApiError(json, "Erreur création challenge");
    return json.challenge;
  },

  async updateChallenge(id: string, data: Record<string, unknown>, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/challenges/${id}`, accountId), {
      method: "PUT",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw challengeApiError(json, "Erreur mise à jour challenge");
    return json.challenge;
  },

  async deleteChallenge(id: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/challenges/${id}`, accountId), {
      method: "DELETE",
    });
    const json = await res.json();
    if (!res.ok) throw challengeApiError(json, "Erreur suppression challenge");
    return json.deleted;
  },

  async activateChallenge(id: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/challenges/${id}/activate`, accountId), {
      method: "PUT",
    });
    const json = await res.json();
    if (!res.ok) throw challengeApiError(json, "Erreur activation challenge");
    return json.challenge;
  },

  async pauseChallenge(id: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/challenges/${id}/pause`, accountId), {
      method: "PUT",
    });
    const json = await res.json();
    if (!res.ok) throw challengeApiError(json, "Erreur pause challenge");
    return json.challenge;
  },

  /** Reprise d'un challenge en pause (date de fin inchangée). */
  async resumeChallenge(id: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/challenges/${id}/resume`, accountId), {
      method: "PUT",
    });
    const json = await res.json();
    if (!res.ok) throw challengeApiError(json, "Erreur reprise challenge");
    return json;
  },

  async endChallenge(id: string, accountId?: string) {
    const res = await apiFetch(accountUrl(`/api/account/challenges/${id}/end`, accountId), {
      method: "PUT",
    });
    const json = await res.json();
    if (!res.ok) throw challengeApiError(json, "Erreur fin challenge");
    return json.challenge;
  },
};

export const categoriesApi = {
  async getAll() {
    const res = await apiFetch("/api/categories");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement catégories");
    return json.categories as Array<{
      id: string;
      name: string;
      labelFr: string;
      labelEn: string;
      color: string | null;
      textColor: string | null;
      sortOrder: number;
    }>;
  },
};

export const adminSessionsHistoryApi = {
  async getAccounts(): Promise<Array<{ id: string; name: string }>> {
    const res = await apiFetch("/api/admin/sessions-history/accounts");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement comptes");
    return json.accounts;
  },

  async getUsers(accountId: string): Promise<Array<{ id: string; firstName: string | null; lastName: string | null; email: string; profileType: string }>> {
    const res = await apiFetch(`/api/admin/sessions-history/users?accountId=${encodeURIComponent(accountId)}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement utilisateurs");
    return json.users;
  },

  async getSimulators(userId: string): Promise<Array<{ id: string; name: string; icon: string | null; simulatorType: string | null; sessionCount: number }>> {
    const res = await apiFetch(`/api/admin/sessions-history/simulators?userId=${encodeURIComponent(userId)}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement simulateurs");
    return json.simulators;
  },

  async getSessions(filters: {
    accountId: string;
    userId?: string;
    simulatorId?: string;
    from?: string;
    to?: string;
    limit?: number;
  }) {
    const params = new URLSearchParams({ accountId: filters.accountId });
    if (filters.userId) params.set("userId", filters.userId);
    if (filters.simulatorId) params.set("simulatorId", filters.simulatorId);
    if (filters.from) params.set("from", filters.from);
    if (filters.to) params.set("to", filters.to);
    if (filters.limit) params.set("limit", String(filters.limit));
    const res = await apiFetch(`/api/admin/sessions-history?${params.toString()}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement sessions");
    return json.sessions;
  },
};

export const adminApi = {
  async getTables() {
    const res = await apiFetch("/api/admin/tables");
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.loadTablesError")));
    return json.tables;
  },

  async updateRecord(table: string, id: string, data: Record<string, unknown>) {
    const res = await apiFetch(`/api/admin/tables/${table}/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.adminUpdateError")));
    return json.record;
  },

  async deleteRecord(table: string, id: string) {
    const res = await apiFetch(`/api/admin/tables/${table}/${id}`, {
      method: "DELETE",
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.adminDeleteError")));
    return json.deleted;
  },

  async createRecord(table: string, data: Record<string, unknown>) {
    const res = await apiFetch(`/api/admin/tables/${table}`, {
      method: "POST",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.adminCreateError")));
    return json.record;
  },

  // Hiérarchie d'entités : rattacher/détacher un compte à un parent (validation anti-cycle/profondeur côté serveur).
  async setAccountParent(accountId: string, parentClientAccountId: string | null) {
    const res = await apiFetch(`/api/admin/accounts/${accountId}/parent`, {
      method: "PUT",
      body: JSON.stringify({ parentClientAccountId }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.adminUpdateError")));
    return json as { ok: true; accountId: string; parentClientAccountId: string | null };
  },

  async getAccountChildren(accountId: string) {
    const res = await apiFetch(`/api/admin/accounts/${accountId}/children`);
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.adminUpdateError")));
    return (json.children || []) as Array<{ id: string; name: string; isActive: boolean }>;
  },

  async duplicateRecord(table: string, id: string) {
    const res = await apiFetch(`/api/admin/tables/${table}/${id}/duplicate`, {
      method: "POST",
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.adminDuplicateError")));
    return json.record as Record<string, unknown>;
  },

  async createCustomLicence(payload: {
    licence: { code: string; name: string; description?: string | null; color?: string | null };
    simulatorIds: string[];
    grant: {
      clientAccountId: string;
      quantity: number;
      durationMonths?: number | null;
      rateLimitMode?: "monthly" | "one_shot" | null;
      rateLimitScope?: "per_user" | "per_account" | null;
      rateLimitSessions?: number | null;
      note?: string | null;
    };
  }) {
    const res = await apiFetch(`/api/admin/custom-licences`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("customLicenceWizard.errorGeneric"));
    return json as {
      licence: { id: string; code: string; name: string };
      simulatorLicences: Array<{ id: string; simulatorId: string }>;
      grant: { id: string; quantity: number; rateLimitSessions: number | null };
    };
  },

  // ----- Trial invitations + coupons (cf. spec/trial-mode-with-coupon.md — option B 27/05/2026) -----
  async listTrialInvitations(status?: string) {
    const qs = status ? `?status=${encodeURIComponent(status)}` : "";
    const res = await apiFetch(`/api/admin/trial-invitations${qs}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement invitations");
    return json.invitations as Array<{
      id: string;
      prospectEmail: string;
      prospectFirstName: string | null;
      prospectName: string | null;
      licenceId: string;
      licenceName: string | null;
      trialCouponCode: string;
      status: string;
      sentAt: string;
      clickedAt: string | null;
      signedUpAt: string | null;
      activatedAt: string | null;
      declinedAt: string | null;
      expiresAt: string | null;
      disabledAt: string | null;
      lastReminderAt: string | null;
      notes: string | null;
      createdAt: string;
    }>;
  },

  async createTrialInvitation(params: {
    prospectEmail: string;
    prospectFirstName?: string;
    prospectName?: string;
    licenceId: string;
    trialCouponCode: string;
    expiresAt?: string;
    notes?: string;
  }) {
    const res = await apiFetch(`/api/admin/trial-invitations`, {
      method: "POST",
      body: JSON.stringify(params),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur lors de la création de l'invitation");
    return json.invitation;
  },

  async resendTrialInvitation(id: string) {
    const res = await apiFetch(`/api/admin/trial-invitations/${id}/resend`, { method: "POST" });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur lors de la relance");
    return json.invitation;
  },

  async disableTrialInvitation(id: string) {
    const res = await apiFetch(`/api/admin/trial-invitations/${id}/disable`, { method: "PATCH" });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur lors de la désactivation");
    return json.invitation;
  },

  async listTrialCoupons() {
    const res = await apiFetch(`/api/admin/trial-coupons`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement coupons");
    return json.coupons as Array<{
      code: string;
      trialSessionsLimit: number;
      maxUses: number | null;
      usesCount: number;
      expiresAt: string | null;
      isActive: boolean;
      note: string | null;
      createdAt: string;
    }>;
  },

  async createTrialCoupon(params: {
    code: string;
    trialSessionsLimit: number;
    maxUses?: number | null;
    expiresAt?: string;
    note?: string;
  }) {
    const res = await apiFetch(`/api/admin/trial-coupons`, {
      method: "POST",
      body: JSON.stringify(params),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur création coupon");
    return json.coupon;
  },

  async updateTrialCoupon(code: string, patch: { isActive?: boolean; maxUses?: number | null; expiresAt?: string | null; note?: string | null }) {
    const res = await apiFetch(`/api/admin/trial-coupons/${encodeURIComponent(code)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur modification coupon");
    return json.coupon;
  },

  async deleteTrialCoupon(code: string) {
    const res = await apiFetch(`/api/admin/trial-coupons/${encodeURIComponent(code)}`, {
      method: "DELETE",
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur suppression coupon");
    return json;
  },

  async getZohoPlans() {
    const res = await apiFetch("/api/admin/zoho/plans");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement plans Zoho");
    return json.plans as Array<{
      planCode: string;
      planId: string;
      name: string;
      productName: string;
      status: string;
      recurringPrice: number;
      interval: number;
      intervalUnit: string;
      unit: string;
    }>;
  },

  async getAccountGrants(accountId: string) {
    const res = await apiFetch(`/api/admin/account-grants?accountId=${accountId}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.loadLicencesError"));
    return json.grants as Array<{
      id: string;
      clientAccountId: string;
      licenceId: string;
      quantity: number;
      expiresAt: string | null;
      grantedBy: string | null;
      note: string | null;
      createdAt: string;
      granterFirstName: string | null;
      granterLastName: string | null;
      licenceName: string | null;
      licenceCode: string | null;
      licenceColor: string | null;
    }>;
  },

  async createAccountGrant(data: {
    clientAccountId: string;
    licenceId: string;
    quantity: number;
    expiresAt?: string | null;
    note?: string;
  }) {
    const res = await apiFetch("/api/admin/account-grants", {
      method: "POST",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.adminCreateError"));
    return json.grant;
  },

  async updateAccountGrant(id: string, data: {
    quantity?: number;
    expiresAt?: string | null;
    note?: string;
  }) {
    const res = await apiFetch(`/api/admin/account-grants/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.adminUpdateError"));
    return json.grant;
  },

  async deleteAccountGrant(id: string) {
    const res = await apiFetch(`/api/admin/account-grants/${id}`, {
      method: "DELETE",
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.adminDeleteError"));
    return json.deleted;
  },

  async getTrash() {
    const res = await apiFetch("/api/admin/trash");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement poubelle");
    return json.items as Array<{
      id: string;
      sourceTable: string;
      sourceId: string;
      displayName: string;
      deletedAt: string;
      expiresAt: string;
      deletedBy: string | null;
      deletedByEmail: string | null;
      deletedByFirstName: string | null;
      childCount: number;
    }>;
  },

  async restoreTrash(id: string) {
    const res = await apiFetch(`/api/admin/trash/${id}/restore`, { method: "PUT" });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur restauration");
    return json.restored;
  },

  async permanentDeleteTrash(id: string) {
    const res = await apiFetch(`/api/admin/trash/${id}`, { method: "DELETE" });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur suppression définitive");
    return json.deleted;
  },

  async syncInternalUsers() {
    const res = await apiFetch("/api/admin/klive/sync-internal", {
      method: "POST",
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.syncInternalError"));
    return json as {
      synced: { created: number; updated: number; linked: number };
      details: Array<{ email: string; action: string; profileType: string }>;
      errors: string[];
    };
  },

  async translateSimulatorsBatch() {
    const res = await apiFetch("/api/admin/i18n/translate-simulators", {
      method: "POST",
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur lors de la traduction batch");
    return json as { ok: true; scanned: number; translated: number };
  },

  async translatePersonaeBatch() {
    const res = await apiFetch("/api/admin/i18n/translate-personae", {
      method: "POST",
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur lors de la traduction batch");
    return json as { ok: true; scanned: number; translated: number };
  },

  async translateOffersBatch() {
    const res = await apiFetch("/api/admin/i18n/translate-offers", {
      method: "POST",
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur lors de la traduction batch");
    return json as { ok: true; scanned: number; translated: number };
  },

  async translateCriteriaBatch() {
    const res = await apiFetch("/api/admin/i18n/translate-criteria", {
      method: "POST",
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur lors du rattrapage des traductions de critères");
    return json as { ok: true; started: true };
  },

  async translateContextsBatch() {
    const res = await apiFetch("/api/admin/i18n/translate-contexts", {
      method: "POST",
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur lors de la traduction batch");
    return json as { ok: true; started: true };
  },

  async getAuditLog(params?: { table?: string; recordId?: string; userId?: string; from?: string; to?: string; limit?: number; offset?: number }) {
    const qs = new URLSearchParams();
    if (params?.table) qs.set("table", params.table);
    if (params?.recordId) qs.set("recordId", params.recordId);
    if (params?.userId) qs.set("userId", params.userId);
    if (params?.from) qs.set("from", params.from);
    if (params?.to) qs.set("to", params.to);
    if (params?.limit) qs.set("limit", String(params.limit));
    if (params?.offset) qs.set("offset", String(params.offset));
    const res = await apiFetch(`/api/admin/audit-log?${qs.toString()}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement audit log");
    return json as { entries: AuditLogEntry[]; count: number; limit: number; offset: number };
  },

  async getRequestLog(params?: { userId?: string; method?: string; path?: string; from?: string; to?: string; limit?: number; offset?: number }) {
    const qs = new URLSearchParams();
    if (params?.userId) qs.set("userId", params.userId);
    if (params?.method) qs.set("method", params.method);
    if (params?.path) qs.set("path", params.path);
    if (params?.from) qs.set("from", params.from);
    if (params?.to) qs.set("to", params.to);
    if (params?.limit) qs.set("limit", String(params.limit));
    if (params?.offset) qs.set("offset", String(params.offset));
    const res = await apiFetch(`/api/admin/request-log?${qs.toString()}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement request log");
    return json as { entries: RequestLogEntry[]; count: number; limit: number; offset: number };
  },
};

export const aiApi = {
  async chat(agentCode: string, messages: Array<{ role: string; content: string }>, context?: { companyDescription?: string; offers?: string }) {
    const res = await apiFetch("/api/ai/chat", {
      method: "POST",
      body: JSON.stringify({ agentCode, messages, context }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur agent IA");
    return json as { message: string; usage: { inputTokens: number; outputTokens: number } };
  },
};

export interface AuditLogEntry {
  id: string;
  userId: string | null;
  userEmail: string | null;
  action: string;
  tableName: string;
  recordId: string;
  oldValues: Record<string, unknown> | null;
  newValues: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface RequestLogEntry {
  id: string;
  method: string;
  path: string;
  handlerName: string | null;
  statusCode: number | null;
  durationMs: number | null;
  userId: string | null;
  userEmail: string | null;
  asUserId: string | null;
  asUserEmail: string | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
}

export interface InboxNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  ctaUrl: string | null;
  ctaLabel: string | null;
  readAt: string | null;
  createdAt: string;
}

export const notificationsApi = {
  async getInbox(opts: { unreadOnly?: boolean; type?: string; limit?: number; offset?: number } = {}): Promise<{ items: InboxNotification[]; unreadCount: number }> {
    const qs = new URLSearchParams();
    if (opts.unreadOnly) qs.set("unreadOnly", "true");
    if (opts.type) qs.set("type", opts.type);
    if (opts.limit !== undefined) qs.set("limit", String(opts.limit));
    if (opts.offset !== undefined) qs.set("offset", String(opts.offset));
    const res = await apiFetch(`/api/notifications/inbox${qs.toString() ? `?${qs}` : ""}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement notifications");
    return json;
  },

  async markRead(id: string): Promise<void> {
    const res = await apiFetch(`/api/notifications/inbox/${id}/read`, { method: "POST" });
    if (!res.ok) {
      const json = await res.json().catch(() => ({}));
      throw new Error(json.error || "Erreur marquage lu");
    }
  },

  async markAllRead(): Promise<void> {
    const res = await apiFetch("/api/notifications/inbox/read-all", { method: "POST" });
    if (!res.ok) {
      const json = await res.json().catch(() => ({}));
      throw new Error(json.error || "Erreur marquage tout lu");
    }
  },

  async getMyPreferences(): Promise<EffectivePreferences> {
    const res = await apiFetch("/api/users/me/notification-preferences");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement préférences");
    return json;
  },

  async updateMyPreferences(updates: Array<{ type: string; channel: string; enabled: boolean }>): Promise<EffectivePreferences> {
    const res = await apiFetch("/api/users/me/notification-preferences", {
      method: "PUT",
      body: JSON.stringify({ updates }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur sauvegarde préférences");
    return json;
  },

  async setChannelLevel(channel: string, level: string): Promise<EffectivePreferences> {
    const res = await apiFetch("/api/users/me/notification-preferences", {
      method: "PUT",
      body: JSON.stringify({ levels: { [channel]: level } }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur sauvegarde préférences");
    return json;
  },

  async getCompanySettings(companyId: string): Promise<CompanyNotificationSettings> {
    const res = await apiFetch(`/api/companies/${companyId}/notification-settings`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement préfs entreprise");
    return json;
  },

  async updateCompanySettings(companyId: string, updates: Array<{ type: string; channel: string; enabledForAccount: boolean }>): Promise<CompanyNotificationSettings> {
    const res = await apiFetch(`/api/companies/${companyId}/notification-settings`, {
      method: "PUT",
      body: JSON.stringify({ updates }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur sauvegarde préfs entreprise");
    return json;
  },
};

export interface CompanyPreferenceCell {
  type: string;
  channel: "email" | "push" | "inapp";
  enabledForAccount: boolean;
  default: boolean;
}

export interface CompanyNotificationSettings {
  companyId: string;
  companyName: string;
  cells: CompanyPreferenceCell[];
}

export interface StreakInfo {
  current: number;
  longest: number;
  lastActiveDate: string | null;
}

export const streakApi = {
  async getMine(): Promise<StreakInfo> {
    const res = await apiFetch("/api/users/me/streak");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur streak");
    return json;
  },
};

// === Rythme hebdomadaire (réutilisabilité — Étape 1) ===

export interface WeeklyRhythmHistoryWeek {
  weekStart: string;
  sessionsCount: number;
  target: number;
  goalMet: boolean;
}
export interface WeeklyRhythm {
  currentWeek: {
    weekStart: string; sessionsCount: number; target: number; goalMet: boolean; remaining: number;
    /** Semaine où l'objectif monte : la série se tient avec l'ancien objectif (`streakTarget`). */
    streakTarget?: number; targetRaised?: boolean; heldForStreak?: boolean;
  };
  streak: { current: number; longest: number };
  history: WeeklyRhythmHistoryWeek[];
}

export const weeklyRhythmApi = {
  async getMine(): Promise<WeeklyRhythm> {
    const res = await apiFetch("/api/users/me/weekly-rhythm");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur rythme hebdo");
    return json;
  },
};

// === Profil de compétence (réutilisabilité — Étape 4) ===

export interface CompetencyCriterion {
  key: string;
  labelFr: string;
  labelEn: string;
  score: number;
  confidence: number;
  toReview?: boolean; // Étape 11
}
export interface CompetencyPhase {
  phaseKey: string;
  score: number | null;
  confidence: number;
  trend: number | null;
  timeToSkillWeeks: number | null;
  criteria: CompetencyCriterion[];
}
export interface CompetencyToReview { phaseKey: string; key: string; labelFr: string; labelEn: string; score: number }

export const competencyApi = {
  async getMine(): Promise<{ phases: CompetencyPhase[]; toReview?: CompetencyToReview[] }> {
    const res = await apiFetch("/api/users/me/competency-profile");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur profil de compétence");
    return json;
  },
  // Diagnostic isolé aux sessions du parcours d'évaluation (instantané figé).
  async getEvaluationDiagnostic(pathId: string): Promise<{ phases: CompetencyPhase[]; toReview?: CompetencyToReview[] }> {
    const res = await apiFetch(`/api/users/me/evaluation-diagnostic?pathId=${encodeURIComponent(pathId)}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur diagnostic");
    return json;
  },
};

export interface RecommendedSimulator {
  simulator: { id: string; name: string; icon: string | null; image: string | null; color: string | null } | null;
  phaseKey: string | null;
  reason: "rising" | "focus" | "starter";
}
export const recommendedSimulatorApi = {
  async getMine(): Promise<RecommendedSimulator> {
    const res = await apiFetch("/api/users/me/recommended-simulator");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur reco simulateur");
    return json;
  },
};

// === Relance intelligente (réutilisabilité — Étape 5) ===

export interface RelanceCardData {
  eventId: string;
  primary: { axis: string; questionFr: string; questionEn: string; buttonFr: string; buttonEn: string; color: string };
  secondary: { axis: string; textFr: string; textEn: string } | null;
  weakestCriterionKey: string | null;
  difficulty: string | null;
  nextDifficulty: string;
  targetId: string | null;
}

export const relanceApi = {
  async getForSession(sessionId: string): Promise<RelanceCardData | null> {
    const res = await apiFetch(`/api/sessions/${sessionId}/relance`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur relance");
    return json.card;
  },
  async act(eventId: string, chosenAxis: string): Promise<void> {
    await apiFetch(`/api/relance/${eventId}/act`, { method: "POST", body: JSON.stringify({ chosenAxis }) });
  },
  // Re-train ciblé (Étape 6) : focus de la session + delta du critère avant/après.
  async getFocus(sessionId: string): Promise<SessionFocusData> {
    const res = await apiFetch(`/api/sessions/${sessionId}/focus`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur focus");
    return json;
  },
};

export interface SessionFocusData {
  focus: { key: string; labelFr: string; labelEn: string } | null;
  delta: { before: number | null; after: number | null; delta: number | null } | null;
}

// Admin — règles de relance (config administrable)
export interface RelanceRule {
  id: string;
  priority: number;
  applicableSimulatorIds: string[];
  conditions: {
    objectiveAchieved?: boolean; scoreMin?: number; scoreMax?: number; hasWeakCriterion?: boolean;
    // Étape 7 — source (défaut simulator) + seuil « critère faible » du pont RDV réel.
    sourceType?: "simulator" | "real_meeting"; weakBelow?: number;
  };
  primaryAxis: string;
  primaryQuestionFr: string;
  primaryQuestionEn: string;
  primaryButtonFr: string;
  primaryButtonEn: string;
  primaryColor: string;
  secondaryAxis: string | null;
  secondaryTextFr: string | null;
  secondaryTextEn: string | null;
  active: boolean;
}

export const relanceRulesApi = {
  async list(): Promise<{ rules: RelanceRule[]; actions: string[] }> {
    const res = await apiFetch("/api/admin/relance-rules");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur règles");
    return json;
  },
  async create(rule: Partial<RelanceRule>): Promise<RelanceRule> {
    const res = await apiFetch("/api/admin/relance-rules", { method: "POST", body: JSON.stringify(rule) });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur création");
    return json.rule;
  },
  async update(id: string, rule: Partial<RelanceRule>): Promise<RelanceRule> {
    const res = await apiFetch(`/api/admin/relance-rules/${id}`, { method: "PUT", body: JSON.stringify(rule) });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur mise à jour");
    return json.rule;
  },
  async remove(id: string): Promise<void> {
    const res = await apiFetch(`/api/admin/relance-rules/${id}`, { method: "DELETE" });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(j.error || "Erreur suppression");
    }
  },
};

// === Routeur — priorité de la semaine (vision engagement #4) ===

export interface WeeklyPriority {
  ruleCode: string | null;
  navTarget: string;
  accentColor: string;
  eyebrow: string | null;
  title: string;
  body: string;
  cta: string;
}

export interface ProgramRule {
  id: string;
  code: string;
  priority: number;
  active: boolean;
  matchContext: string | null;
  matchLevel: string | null;
  matchGraph: string | null;
  matchActivePath: string | null;
  navTarget: string;
  accentColor: string;
  eyebrowFr: string | null;
  eyebrowEn: string | null;
  titleFr: string;
  titleEn: string;
  bodyFr: string | null;
  bodyEn: string | null;
  ctaFr: string;
  ctaEn: string;
}

export const weeklyPriorityApi = {
  async getMine(): Promise<WeeklyPriority> {
    const res = await apiFetch("/api/users/me/weekly-priority");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur priorité");
    return json.priority;
  },
};

export interface ResumeTarget {
  kind: "path" | "duel" | "session";
  label: string;
  navTarget: string;
  payload: Record<string, string>;
}

export const resumeApi = {
  async getMine(): Promise<ResumeTarget | null> {
    const res = await apiFetch("/api/users/me/resume");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur reprise");
    return json.resume;
  },
};

export const programRulesApi = {
  async list(): Promise<{ rules: ProgramRule[]; navTargets: string[] }> {
    const res = await apiFetch("/api/admin/program-rules");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur règles");
    return json;
  },
  async create(rule: Partial<ProgramRule>): Promise<ProgramRule> {
    const res = await apiFetch("/api/admin/program-rules", { method: "POST", body: JSON.stringify(rule) });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur création");
    return json.rule;
  },
  async update(id: string, rule: Partial<ProgramRule>): Promise<ProgramRule> {
    const res = await apiFetch(`/api/admin/program-rules/${id}`, { method: "PUT", body: JSON.stringify(rule) });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur mise à jour");
    return json.rule;
  },
  async remove(id: string): Promise<void> {
    const res = await apiFetch(`/api/admin/program-rules/${id}`, { method: "DELETE" });
    if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.error || "Erreur suppression"); }
  },
};

// === Sales Coach IA Réel (chantier 2.1) ===
import type {
  RealCallSessionListItem,
  RealCallSessionFull,
  RealCallContextType,
} from "../types";

export type CreateFromTranscriptPayload = {
  transcriptText: string;
  contextType?: RealCallContextType;
  simulatorId?: string;
  contextId?: string;
  contextProspectName?: string;
  contextProspectCompany?: string;
  contextNotes?: string;
  speakerMapping: { commercialSpeaksFirst: boolean };
  consentConfirmed: boolean;
  consentTextVersion?: string;
};

export type CreateFromAudioPayload = {
  audioBlob: Blob;
  audioFilename: string;
  contextType?: RealCallContextType;
  simulatorId?: string;
  contextId?: string;
  contextProspectName?: string;
  contextProspectCompany?: string;
  contextNotes?: string;
  speakerMapping: { commercialSpeaksFirst: boolean };
  consentConfirmed: boolean;
  consentTextVersion?: string;
  language?: string;
};

// Réutilisabilité — Étape 9 : BizCoins (porte-monnaie + réglages entreprise).
export interface BizWalletData {
  balance: number;
  levelKey: "regular" | "assidu" | "performer";
  multiplier: number;
  mode: "virtual" | "catalog";
  ledger: Array<{ delta: number; reason: string; balanceAfter: number; createdAt: string }>;
}
export interface BizSettingsData {
  rewardsMode: "virtual" | "catalog";
  bizcoinValueCents: number;
  ursaffAckAt: string | null;
  ursaffAckBy: string | null;
}
export const bizCoinsApi = {
  async getWallet(): Promise<BizWalletData> {
    const res = await apiFetch("/api/users/me/biz-wallet");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur porte-monnaie");
    return json;
  },
  async getSettings(): Promise<BizSettingsData> {
    const res = await apiFetch("/api/account/biz-settings");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur réglages");
    return json;
  },
  // Renvoie { ursaffRequired: true } si le passage en mode cadeaux exige l'acquittement URSSAF.
  async saveSettings(body: { rewardsMode: "virtual" | "catalog"; bizcoinValueCents?: number; ursaffAck?: boolean }): Promise<BizSettingsData | { ursaffRequired: true }> {
    const res = await apiFetch("/api/account/biz-settings", { method: "PUT", body: JSON.stringify(body) });
    const json = await res.json();
    if (res.status === 409 && json.error === "URSSAF_ACK_REQUIRED") return { ursaffRequired: true };
    if (!res.ok) throw new Error(json.error || "Erreur enregistrement");
    return json;
  },
  // --- Lot 9B : catalogue (admin), boutique (user), overrides par contexte ---
  async listGifts(): Promise<BizGift[]> {
    const res = await apiFetch("/api/account/biz-gifts");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur catalogue");
    return json.gifts;
  },
  async createGift(body: BizGiftInput): Promise<BizGift> {
    const res = await apiFetch("/api/account/biz-gifts", { method: "POST", body: JSON.stringify(body) });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur création cadeau");
    return json.gift;
  },
  async updateGift(id: string, body: BizGiftInput): Promise<BizGift> {
    const res = await apiFetch(`/api/account/biz-gifts/${id}`, { method: "PUT", body: JSON.stringify(body) });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur modification cadeau");
    return json.gift;
  },
  async deleteGift(id: string): Promise<void> {
    await apiFetch(`/api/account/biz-gifts/${id}`, { method: "DELETE" });
  },
  async getShop(): Promise<{ mode: "virtual" | "catalog"; gifts: BizShopGift[]; wallet: BizWalletData }> {
    const res = await apiFetch("/api/users/me/shop");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur boutique");
    return json;
  },
  // 402 = solde insuffisant, 409 = rupture de stock.
  async redeem(giftId: string): Promise<{ ok: true; balance: number } | { error: "INSUFFICIENT_BALANCE" | "OUT_OF_STOCK" }> {
    const res = await apiFetch("/api/users/me/shop/redeem", { method: "POST", body: JSON.stringify({ giftId }) });
    const json = await res.json();
    if (res.status === 402) return { error: "INSUFFICIENT_BALANCE" };
    if (res.status === 409) return { error: "OUT_OF_STOCK" };
    if (!res.ok) throw new Error(json.error || "Erreur échange");
    return { ok: true, balance: json.balance };
  },
  async setChallengeRewardsMode(challengeId: string, mode: "inherit" | "virtual" | "catalog"): Promise<void> {
    await apiFetch(`/api/challenges/${challengeId}/rewards-mode`, { method: "PUT", body: JSON.stringify({ mode }) });
  },
  async setPathRewardsMode(pathId: string, mode: "inherit" | "virtual" | "catalog"): Promise<void> {
    await apiFetch(`/api/certification-paths/${pathId}/rewards-mode`, { method: "PUT", body: JSON.stringify({ mode }) });
  },
};

export interface BizGift {
  id: string;
  labelFr: string;
  labelEn: string;
  costCoins: number;
  stock: number | null;
  active: boolean;
}
export interface BizGiftInput {
  labelFr: string;
  labelEn: string;
  costCoins: number;
  stock: number | null;
  active: boolean;
}
export interface BizShopGift {
  id: string;
  labelFr: string;
  labelEn: string;
  costCoins: number;
  stock: number | null;
}

// Réutilisabilité — Tournois (extension Vague 3).
export interface TournamentSummary {
  id: string; name: string; format: "league" | "elimination"; status: string;
  prizeCoins: number; participantCount: number; joined: boolean; maxParticipants: number | null;
  /** Périmètre Pilot : qui joue, qui l'a créé. */
  participantIds?: string[]; createdBy?: string | null;
  /** Gestes de gestion permis au lecteur (règle partagée `canOrganizeTournament`, calculée par le serveur). */
  canManage?: boolean;
  // Config
  difficulty?: string; metric?: string; caseMode?: string; roundDeadlineHours?: number;
  simulatorId?: string | null; targetId?: string | null; simulatorName?: string | null;
  // Dates / avancement / vainqueur
  createdAt?: string; startedAt?: string | null; finishedAt?: string | null;
  winnerName?: string | null;
  matchesTotal?: number; matchesDone?: number; currentRound?: number; totalRounds?: number;
}
/**
 * `pilot` : appel depuis Pilot — le compte choisi dans la barre de Pilot accompagne la requête
 * (`withPilotScope`), accepté par le serveur pour un ADM / COO seulement (même règle que la liste).
 */
const tournamentUrl = (path: string, pilot?: boolean) => (pilot ? withPilotScope(path) : path);

export const tournamentsApi = {
  /** `pilot` : Pilot › Planifier › Tournois — compte et périmètre de la barre de Pilot (Q1 du 29/09/2026). */
  async list(opts?: { pilot?: boolean }): Promise<TournamentSummary[]> {
    const res = await apiFetch(opts?.pilot ? withPilotScope("/api/tournaments?pilot=1") : "/api/tournaments");
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("common.loadError")));
    return json.tournaments;
  },
  async get(id: string, opts?: { pilot?: boolean }): Promise<any> {
    const res = await apiFetch(tournamentUrl(`/api/tournaments/${id}`, opts?.pilot));
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("common.loadError")));
    return json;
  },
  async create(body: any, opts?: { pilot?: boolean }): Promise<{ id: string } | { error: string }> {
    const res = await apiFetch(tournamentUrl("/api/tournaments", opts?.pilot), { method: "POST", body: JSON.stringify(body) });
    const json = await res.json();
    if (!res.ok) return { error: apiErrorText(json, i18n.t("common.error")) };
    return json.tournament;
  },
  async join(id: string, opts?: { pilot?: boolean }): Promise<{ ok: boolean; error?: string }> {
    const res = await apiFetch(tournamentUrl(`/api/tournaments/${id}/join`, opts?.pilot), { method: "POST" });
    const json = await res.json();
    return res.ok ? { ok: true } : { ok: false, error: apiErrorText(json, i18n.t("common.error")) };
  },
  async start(id: string, opts?: { pilot?: boolean }): Promise<{ ok: boolean; error?: string }> {
    const res = await apiFetch(tournamentUrl(`/api/tournaments/${id}/start`, opts?.pilot), { method: "POST" });
    const json = await res.json();
    return res.ok ? { ok: true } : { ok: false, error: apiErrorText(json, i18n.t("common.error")) };
  },
  async update(id: string, body: any, opts?: { pilot?: boolean }): Promise<{ id: string } | { error: string }> {
    const res = await apiFetch(tournamentUrl(`/api/tournaments/${id}`, opts?.pilot), { method: "PUT", body: JSON.stringify(body) });
    const json = await res.json();
    if (!res.ok) return { error: apiErrorText(json, i18n.t("common.error")) };
    return json.tournament;
  },
  async manageParticipant(id: string, userId: string, action: "add" | "remove", opts?: { pilot?: boolean }): Promise<{ ok: boolean; error?: string }> {
    const res = await apiFetch(tournamentUrl(`/api/tournaments/${id}/participants`, opts?.pilot), { method: "POST", body: JSON.stringify({ userId, action }) });
    const json = await res.json();
    return res.ok ? { ok: true } : { ok: false, error: apiErrorText(json, i18n.t("common.error")) };
  },
  async remove(id: string, opts?: { pilot?: boolean }): Promise<{ ok: boolean; error?: string }> {
    const res = await apiFetch(tournamentUrl(`/api/tournaments/${id}`, opts?.pilot), { method: "DELETE" });
    const json = await res.json().catch(() => ({}));
    return res.ok ? { ok: true } : { ok: false, error: apiErrorText(json, i18n.t("common.error")) };
  },
};

// Réutilisabilité — §9 télémétrie : KPIs (ADM/COO).
export const reusabilityKpisApi = {
  async getMine(): Promise<any> {
    const res = await apiFetch("/api/admin/reusability-kpis");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur KPIs");
    return json;
  },
};

// Réutilisabilité — Étape 8 : duels 1v1.
export interface DuelData {
  id: string;
  status: "pending" | "accepted" | "played" | "settled" | "declined" | "expired";
  role: "challenger" | "opponent";
  opponentName: string;
  caseName: string | null;
  simulatorId: string | null;
  targetId: string | null;
  difficulty: string;
  stakeCoins: number;
  metric: "objective" | "progression";
  winnerId: string | null;
  iPlayed: boolean;
  opponentPlayed: boolean;
  iWon: boolean;
}
export const duelsApi = {
  async list(): Promise<DuelData[]> {
    const res = await apiFetch("/api/duels");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur duels");
    return json.duels;
  },
  async opponents(): Promise<Array<{ id: string; firstName: string | null; lastName: string | null }>> {
    const res = await apiFetch("/api/duels/opponents");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur");
    return json.opponents;
  },
  async create(body: { opponentId: string; simulatorId: string; targetId?: string | null; difficulty: string; stakeCoins: number; metric: "objective" | "progression" }): Promise<{ duel: DuelData } | { error: string }> {
    const res = await apiFetch("/api/duels", { method: "POST", body: JSON.stringify(body) });
    const json = await res.json();
    // Refus codé (ex. `target_not_available`) : texte dans la langue de l'écran.
    if (!res.ok) return { error: apiErrorText(json, i18n.t("common.error")) };
    return json;
  },
  async accept(id: string): Promise<{ ok: boolean; error?: string }> {
    const res = await apiFetch(`/api/duels/${id}/accept`, { method: "POST" });
    const json = await res.json();
    return res.ok ? { ok: true } : { ok: false, error: json.error };
  },
  async decline(id: string): Promise<{ ok: boolean; error?: string }> {
    const res = await apiFetch(`/api/duels/${id}/decline`, { method: "POST" });
    const json = await res.json();
    return res.ok ? { ok: true } : { ok: false, error: json.error };
  },
  // Étape 10 — chat minimal autour d'un duel.
  async chatList(id: string): Promise<Array<{ id: string; body: string; createdAt: string; mine: boolean }>> {
    const res = await apiFetch(`/api/duels/${id}/chat`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chat");
    return json.messages;
  },
  async chatSend(id: string, body: string): Promise<void> {
    await apiFetch(`/api/duels/${id}/chat`, { method: "POST", body: JSON.stringify({ body }) });
  },
};

// Réutilisabilité — Étape 7 : carte « pont RDV réel → entraînement ».
export interface RealMeetingBridgeCardData {
  primary: { axis: string; questionFr: string; questionEn: string; buttonFr: string; buttonEn: string; color: string };
  secondary: { axis: string; textFr: string; textEn: string } | null;
  // `scoreSurDix` : le score BRUT de la dimension, celui qu'affiche la liste « Scores par
  // dimension » de la fiche. Voir `real-meeting-bridge.ts` pour la raison du renommage.
  weakest: { key: string; labelFr: string; labelEn: string; scoreSurDix: number } | null;
  recommendedSimulatorKey: string | null;
}

export const salesCoachApi = {
  async createFromTranscript(payload: CreateFromTranscriptPayload): Promise<{
    sessionId: string;
    analysisStatus: "in_progress" | "pending";
    contextType: RealCallContextType;
  }> {
    const res = await apiFetch("/api/sales-coach/sessions/transcript", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, "Erreur création session Coach Debrief"));
    return json;
  },

  async createFromAudio(payload: CreateFromAudioPayload): Promise<{
    sessionId: string;
    transcriptionStatus: "pending";
    contextType: RealCallContextType;
  }> {
    const form = new FormData();
    form.append("audio", payload.audioBlob, payload.audioFilename);
    form.append("commercialSpeaksFirst", String(payload.speakerMapping.commercialSpeaksFirst));
    form.append("consentConfirmed", String(payload.consentConfirmed));
    if (payload.contextType) form.append("contextType", payload.contextType);
    if (payload.simulatorId) form.append("simulatorId", payload.simulatorId);
    if (payload.contextId) form.append("contextId", payload.contextId);
    if (payload.contextProspectName) form.append("contextProspectName", payload.contextProspectName);
    if (payload.contextProspectCompany) form.append("contextProspectCompany", payload.contextProspectCompany);
    if (payload.contextNotes) form.append("contextNotes", payload.contextNotes);
    if (payload.consentTextVersion) form.append("consentTextVersion", payload.consentTextVersion);
    if (payload.language) form.append("language", payload.language);

    // ⚠️ Ne pas mettre Content-Type — fetch le calcule auto avec le boundary du FormData
    const res = await apiFetch("/api/sales-coach/sessions/audio", {
      method: "POST",
      body: form,
      headers: {}, // override le Content-Type:application/json par défaut
    });
    // Un refus « trop gros » peut venir du serveur (code `audio_too_large`) ou d'un intermédiaire
    // (413 sans JSON) : dans les deux cas, le message traduit de l'écran, jamais une erreur technique.
    const json = await res.json().catch(() => ({} as { error?: string; code?: string }));
    if (res.status === 413 || json.code === "audio_too_large") throw new Error(i18n.t("salesCoach.newSession.uploadErrorSize"));
    if (!res.ok) throw new Error(apiErrorText(json, "Erreur création session audio"));
    return json;
  },

  async getSession(id: string): Promise<RealCallSessionFull> {
    const res = await apiFetch(`/api/sales-coach/sessions/${id}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Session introuvable");
    return json.session as RealCallSessionFull;
  },

  // Réutilisabilité — Étape 7 : carte « pont RDV réel → entraînement » (ou null).
  async getBridge(id: string): Promise<RealMeetingBridgeCardData | null> {
    const res = await apiFetch(`/api/sales-coach/sessions/${id}/bridge`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur pont");
    return json.card;
  },
  // Pont de la dernière session RDV réel analysée (carte home « Depuis ton dernier RDV réel »).
  async getMyLastBridge(): Promise<RealMeetingBridgeCardData | null> {
    const res = await apiFetch("/api/users/me/last-bridge");
    const json = await res.json();
    if (!res.ok) return null;
    return json.card;
  },

  // Chantier 2.2 — Coaching-brief IA → cas privé persistant
  async createCoachingBrief(sessionId: string, body: {
    simulatorId: string;
    contextId?: string;
    offerId: string;
    personaId: string;
    focusRaw: string;
    phase?: string;
    difficulty?: "easy" | "medium" | "hard";
    // Détails B2B Sales obligatoires côté popup (aligné Valérie commit d0197ad).
    // Optionnels ici : envoyés undefined si la persona est B2C ou Customer Care.
    industry?: string[];
    companySize?: string;
    customIndustry?: string;
  }): Promise<{
    targetId: string;
    scenarioInstruction: string;
    suggestedDifficulty: "easy" | "medium" | "hard";
    displayName: string;
  }> {
    const res = await apiFetch(`/api/sales-coach/sessions/${sessionId}/coaching-brief`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur création coaching-brief");
    return json.brief;
  },

  // V3 — Clips audio partageables
  async listSessionClips(sessionId: string): Promise<Array<{
    id: string; sessionId: string; shareToken: string;
    startSec: number; endSec: number;
    title: string | null; note: string | null;
    expiresAt: string | null; viewCount: number; createdAt: string;
    createdByFirstName: string | null; createdByLastName: string | null;
    shareUrlPath: string;
  }>> {
    const res = await apiFetch(`/api/sales-coach/sessions/${sessionId}/clips`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur clips");
    return json.clips;
  },

  async createSessionClip(sessionId: string, body: { startSec: number; endSec: number; title?: string; note?: string }): Promise<{
    id: string; sessionId: string; shareToken: string; startSec: number; endSec: number;
    title: string | null; note: string | null; expiresAt: string | null;
    viewCount: number; createdAt: string; createdByFirstName: string | null;
    createdByLastName: string | null; shareUrlPath: string;
  }> {
    const res = await apiFetch(`/api/sales-coach/sessions/${sessionId}/clips`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur création clip");
    return json.clip;
  },

  async deleteClip(clipId: string): Promise<void> {
    const res = await apiFetch(`/api/sales-coach/clips/${clipId}`, { method: "DELETE" });
    if (!res.ok) {
      const json = await res.json().catch(() => ({}));
      throw new Error(json.error || "Erreur suppression clip");
    }
  },

  // Public (pas d'auth) — chargé par la page /sales-coach/share/:token
  async getPublicClip(token: string): Promise<{
    startSec: number; endSec: number;
    title: string | null; note: string | null;
    audioUrl: string; audioMimeType: string | null;
    prospectName: string | null; prospectCompany: string | null;
    contextType: string | null; createdByFirstName: string | null;
    createdAt: string;
  }> {
    const res = await fetch(`/api/sales-coach/share/${token}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Clip introuvable");
    return json.clip;
  },

  // V3 — Toggle highlight (Library "appels épinglés")
  async toggleSessionHighlight(sessionId: string, isHighlighted: boolean): Promise<{ id: string; isHighlighted: boolean }> {
    const res = await apiFetch(`/api/sales-coach/sessions/${sessionId}/highlight`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ isHighlighted }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur toggle highlight");
    return json.session;
  },

  // V3 — Notes au timestamp d'un appel
  async listSessionNotes(sessionId: string): Promise<Array<{
    id: string;
    sessionId: string;
    authorUserId: string;
    authorFirstName: string | null;
    authorLastName: string | null;
    startSec: number;
    endSec: number | null;
    body: string;
    kind: "self" | "manager_note" | "highlight";
    createdAt: string;
    updatedAt: string;
    canEdit: boolean;
  }>> {
    const res = await apiFetch(`/api/sales-coach/sessions/${sessionId}/notes`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur chargement notes");
    return json.notes;
  },

  async createSessionNote(sessionId: string, body: { startSec: number; endSec?: number; body: string; kind?: "self" | "manager_note" | "highlight" }): Promise<{
    id: string; startSec: number; endSec: number | null; body: string; kind: string; createdAt: string;
    authorUserId: string; authorFirstName: string | null; authorLastName: string | null; canEdit: boolean; sessionId: string; updatedAt: string;
  }> {
    const res = await apiFetch(`/api/sales-coach/sessions/${sessionId}/notes`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur création note");
    return json.note;
  },

  async deleteSessionNote(noteId: string): Promise<void> {
    const res = await apiFetch(`/api/sales-coach/notes/${noteId}`, { method: "DELETE" });
    if (!res.ok) {
      const json = await res.json().catch(() => ({}));
      throw new Error(json.error || "Erreur suppression note");
    }
  },

  // V3 — URL signée R2 (15 min) pour le player audio synchro
  async getSessionAudioUrl(sessionId: string): Promise<{ url: string; expiresInSec: number }> {
    const res = await apiFetch(`/api/sales-coach/sessions/${sessionId}/audio-url`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "URL audio indisponible");
    return json;
  },

  async listMySessions(limit = 20): Promise<RealCallSessionListItem[]> {
    const res = await apiFetch(`/api/sales-coach/sessions?limit=${limit}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur listing sessions");
    return json.sessions as RealCallSessionListItem[];
  },

  // Liste enrichie filtrable (page liste-tableau Modjo-like)
  async listMySessionsFiltered(filters?: {
    contextType?: string;
    minScore?: number;
    maxScore?: number;
    fromDate?: string;
    toDate?: string;
    search?: string;
    sort?: "date_desc" | "date_asc" | "score_desc" | "score_asc" | "duration_desc";
    highlightedOnly?: boolean;
    methodologyFramework?: string;
    methodologyMinCoveragePct?: number;
    limit?: number;
  }): Promise<Array<{
    id: string;
    contextType: RealCallContextType | null;
    contextProspectName: string | null;
    contextProspectCompany: string | null;
    scoreGlobal: number | null;
    scoreTier: string | null;
    analysisStatus: "pending" | "in_progress" | "completed" | "failed";
    isPrivate: boolean;
    keepIndefinitely: boolean;
    createdAt: string;
    audioDurationSeconds: number | null;
    inputType: "transcript" | "audio" | "video" | null;
    topStrengthTitle: string | null;
    topImprovementTitle: string | null;
    summary: string | null;
    scoreCoachability: number | null;
    speechMetrics: {
      talkRatioCommercialPct: number;
      speechRateCommercialWpm: number;
      openQuestionsCount: number;
    } | null;
    searchSnippets: string[] | null;
    searchMatchCount: number | null;
    isHighlighted: boolean;
    methodologyFramework: string | null;
    methodologyCoveredCount: number | null;
    methodologyTotalCount: number | null;
  }>> {
    const params = new URLSearchParams();
    if (filters?.contextType) params.set("contextType", filters.contextType);
    if (filters?.minScore !== undefined) params.set("minScore", String(filters.minScore));
    if (filters?.maxScore !== undefined) params.set("maxScore", String(filters.maxScore));
    if (filters?.fromDate) params.set("fromDate", filters.fromDate);
    if (filters?.toDate) params.set("toDate", filters.toDate);
    if (filters?.search) params.set("search", filters.search);
    if (filters?.sort) params.set("sort", filters.sort);
    if (filters?.highlightedOnly) params.set("highlightedOnly", "1");
    if (filters?.methodologyFramework) params.set("methodologyFramework", filters.methodologyFramework);
    if (filters?.methodologyMinCoveragePct !== undefined) params.set("methodologyMinCoveragePct", String(filters.methodologyMinCoveragePct));
    if (filters?.limit) params.set("limit", String(filters.limit));
    const res = await apiFetch(`/api/sales-coach/sessions/list${params.toString() ? `?${params}` : ""}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur listing sessions");
    return json.sessions;
  },

  // --- Progression dans le temps (V2 chantier 4) ---
  async getMyProgression(weeks = 12): Promise<Array<{
    weekStart: string;
    count: number;
    avgScore: number | null;
    avgCoachability: number | null;
  }>> {
    const res = await apiFetch(`/api/sales-coach/progression?weeks=${weeks}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur progression");
    return json.progression;
  },

  // --- Library objections récurrentes (V2) ---
  async listMyObjections(): Promise<Array<{
    sessionId: string;
    sessionCreatedAt: string;
    prospectName: string | null;
    prospectCompany: string | null;
    objection: string;
    handling: "well_handled" | "poorly_handled" | "evaded";
    handlingCitation: string | null;
    suggestion: string | null;
  }>> {
    const res = await apiFetch("/api/sales-coach/library/objections");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur library objections");
    return json.objections;
  },

  // --- Manager view ---
  async listTeamSessions(filters?: {
    userId?: string; minScore?: number; contextType?: string; fromDate?: string; toDate?: string; limit?: number;
  }): Promise<Array<{
    id: string; userId: string; userFirstName: string | null; userLastName: string | null;
    contextType: RealCallContextType; contextProspectName: string | null; contextProspectCompany: string | null;
    scoreGlobal: number | null; scoreTier: string | null;
    analysisStatus: "pending" | "in_progress" | "completed" | "failed";
    inputType: "transcript" | "audio" | "video";
    audioDurationSeconds: number | null; isPrivate: boolean; createdAt: string;
  }>> {
    const params = new URLSearchParams();
    if (filters?.userId) params.set("userId", filters.userId);
    if (filters?.minScore !== undefined) params.set("minScore", String(filters.minScore));
    if (filters?.contextType) params.set("contextType", filters.contextType);
    if (filters?.fromDate) params.set("fromDate", filters.fromDate);
    if (filters?.toDate) params.set("toDate", filters.toDate);
    if (filters?.limit) params.set("limit", String(filters.limit));
    const res = await apiFetch(`/api/sales-coach/team/sessions${params.toString() ? `?${params}` : ""}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur listing équipe");
    return json.sessions;
  },

  // V2 chantier 5 — Coaching cards manager
  async getCoachingCards(): Promise<Array<{
    userId: string;
    userName: string;
    sessionsCount: number;
    avgScore: number | null;
    poorlyHandledObjectionsCount: number;
    topRecommendedSimulator: { key: string; count: number } | null;
    callsToReview: Array<{
      sessionId: string;
      createdAt: string;
      prospectName: string | null;
      prospectCompany: string | null;
      scoreGlobal: number | null;
      topImprovementTitle: string | null;
    }>;
  }>> {
    const res = await apiFetch("/api/sales-coach/team/coaching-cards");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur coaching cards");
    return json.cards;
  },

  async getTeamStats(): Promise<{ totalSessions: number; averageScore: number | null; activeCommercials: number }> {
    const res = await apiFetch("/api/sales-coach/team/stats");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Erreur stats équipe");
    return json.stats;
  },
};

export type NotifChannel = "email" | "push" | "inapp";
export type NotifChannelLevel = "all" | "essential" | "off";

export interface PreferenceCell {
  type: string;
  channel: NotifChannel;
  effective: boolean;
  default: boolean;
  userValue: boolean | null;
  accountOverride: boolean | null;
  lockedByAccount: boolean;
  /** Toujours envoyé : ni vous ni votre entreprise ne pouvez le couper. */
  alwaysOn?: boolean;
}

export interface EffectivePreferences {
  cells: PreferenceCell[];
  clientAccountId: string | null;
  channelLevels: Record<NotifChannel, NotifChannelLevel>;
}

/** Banque de cas Kestio — couverture et habillage sectoriel (ADM/COO). */
export const banqueDeCasApi = {
  async get() {
    const res = await apiFetch("/api/admin/banque-de-cas");
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.loadError"));
    return json;
  },
  /** Fiche complète d'un cas : contenu, pédagogie, et le scénario tel qu'il sera joué. */
  async detail(targetId: string) {
    const res = await apiFetch(`/api/admin/banque-de-cas/${targetId}`);
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.loadError"));
    return json;
  },
  async habiller(targetId: string, secteurs: string[], profondeur: "lexical" | "contextual") {
    const res = await apiFetch(`/api/admin/banque-de-cas/${targetId}/habiller`, {
      method: "POST", body: JSON.stringify({ secteurs, profondeur }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.updateError"));
    return json;
  },
  /** La file de relecture : les couples cas × simulateur jouables dont le brief n'est pas relu. */
  async fileRelecture() {
    const res = await apiFetch(`/api/admin/banque-de-cas/relecture`);
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("adminUi.banque.failed")));
    return json as { file: Array<{ targetId: string; version: string | null; titre: string; statut: string;
      simulatorId: string; simulateur: string; niveau: string; relu: boolean; aEcrire: number }> };
  },
  /**
   * Enregistrer les blocs d'un simulateur ; `valider` les marque relus et actifs en session.
   * `blocsEn` est facultatif : omis, l'anglais des blocs dont le français a changé est remis à
   * vide côté serveur, pour que le traducteur le refasse au lieu de laisser une version périmée.
   */
  async setBrief(targetId: string, simulatorId: string, blocs: Record<string, string>, valider: boolean, blocsEn?: Record<string, string>) {
    const res = await apiFetch(`/api/admin/banque-de-cas/${targetId}/briefs/${simulatorId}`, {
      method: "PUT", body: JSON.stringify({ blocs, blocsEn, valider }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.updateError"));
    return json;
  },
  /** Réécrire à la main une face du scénario. Une face absente du corps n'est pas touchée ;
   *  une chaîne vide revient au texte composé depuis les champs du cas. */
  async setTexte(targetId: string, langue: "fr" | "en", faces: { visible?: string; agent?: string }) {
    const res = await apiFetch(`/api/admin/banque-de-cas/${targetId}/texte`, {
      method: "PUT", body: JSON.stringify({ langue, ...faces }),
    });
    const json = await res.json().catch(() => ({}));
    // Refus codé : affiché dans la langue de l'écran (`apiErrors.<code>`).
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("api.updateError")));
    return json;
  },
  /**
   * Réécrit ce que reçoit l'agent pour UN simulateur (texte vide = retour au texte automatique).
   * Refus 422 si une balise indispensable manque : l'erreur porte `erreurs` (codes traduits par
   * l'écran) — rien n'est écrit.
   */
  async setTexteAgent(targetId: string, simulatorId: string, langue: "fr" | "en", texte: string) {
    const res = await apiFetch(`/api/admin/banque-de-cas/${targetId}/agent/${simulatorId}`, {
      method: "PUT", body: JSON.stringify({ langue, texte }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err: any = new Error(apiErrorText(json, i18n.t("api.updateError")));
      err.erreurs = json.erreurs ?? [];
      err.avertissements = json.avertissements ?? [];
      throw err;
    }
    return json as { ok: true; avertissements: Array<{ code: string; balise?: string }> };
  },
  async setStatut(targetId: string, status: string) {
    const res = await apiFetch(`/api/admin/banque-de-cas/${targetId}/statut`, {
      method: "PUT", body: JSON.stringify({ status }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.updateError"));
    return json;
  },
  /** Enregistre qu'un humain a relu la traduction (ou retire la marque pour la corriger).
   *  Ne débloque rien : le cas était déjà publié. Sert à savoir ce qui a été lu. */
  async validerTraduction(targetId: string, valide: boolean) {
    const res = await apiFetch(`/api/admin/banque-de-cas/${targetId}/traduction-relue`, {
      method: "POST", body: JSON.stringify({ valide }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.updateError"));
    return json as { i18nSource: string; i18nReviewedAt: string | null };
  },
  /** Traduit le cas, son persona, son offre et ses briefs. Idempotent : ne réécrit jamais un
   *  texte anglais déjà là — relancer après une correction manuelle la préserve. */
  async traduire(targetId: string) {
    const res = await apiFetch(`/api/admin/banque-de-cas/${targetId}/traduire`, { method: "POST" });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || i18n.t("api.updateError"));
    return json as { complet: boolean; champsCas: string[]; blocsTraduits: number; manques: string[] };
  },
};

/** Portefeuilles consultants (ADM) : qui suit quel compte. */
export const portfoliosApi = {
  async get(): Promise<{ consultants: { id: string; name: string; email: string | null; status: string | null }[]; accounts: { id: string; name: string }[]; links: { id: string; userId: string; clientAccountId: string; createdAt: string }[] }> {
    const res = await apiFetch("/api/admin/portfolios");
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.portfoliosLoad")));
    return json;
  },
  async set(userId: string, accountIds: string[]): Promise<{ ok: boolean; added: number; removed: number; accountIds: string[] }> {
    const res = await apiFetch(`/api/admin/portfolios/${userId}`, { method: "PUT", body: JSON.stringify({ accountIds }) });
    const json = await res.json();
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.saveFailed")));
    return json;
  },
};

/** Documents légaux (CGV/CGU, DPA) — versions déposées par l'ADM (plan feat-acceptation-cgv-dpa, lot 1). */
export type LegalDocumentKindApi = "cgv" | "dpa" | "rh_annex" | "privacy";
export interface LegalVersionApi {
  id: string;
  kind: LegalDocumentKindApi;
  version: string;
  effectiveOn: string;
  fileName: string;
  pdfSha256: string;
  pdfSize: number;
  withdrawnAt: string | null;
  createdAt: string;
  status: "current" | "scheduled" | "superseded" | "withdrawn";
}
export interface LegalAcceptanceApi {
  id: string;
  submissionId: string;
  context: "signup" | "in_app";
  kind: Exclude<LegalDocumentKindApi, "privacy">;
  documentVersionId: string;
  documentVersion: string;
  documentSha256: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  profileType: string;
  companyName: string;
  companyRegistrationKind: "siren" | "foreign" | "unknown";
  companyRegistration: string | null;
  checkboxText: string;
  language: string;
  ip: string | null;
  userAgent: string | null;
  acceptedAt: string;
  clientAccountId: string | null;
  accountName: string | null;
  linkedAt: string | null;
}
export const legalDocumentsApi = {
  async adminList(): Promise<{ today: string; versions: LegalVersionApi[] }> {
    const res = await apiFetch("/api/admin/legal-documents");
    const json = await res.json().catch(() => null);
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("common.loadError")));
    return json;
  },
  async adminCreate(input: { kind: LegalDocumentKindApi; version: string; effectiveOn: string; fileName: string; pdfBase64: string }): Promise<void> {
    const res = await apiFetch("/api/admin/legal-documents", { method: "POST", body: JSON.stringify(input) });
    const json = await res.json().catch(() => null);
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.saveFailed")));
  },
  async adminWithdraw(id: string): Promise<void> {
    const res = await apiFetch(`/api/admin/legal-documents/${id}/withdraw`, { method: "POST" });
    const json = await res.json().catch(() => null);
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.saveFailed")));
  },
  async adminRestore(id: string): Promise<void> {
    const res = await apiFetch(`/api/admin/legal-documents/${id}/restore`, { method: "POST" });
    const json = await res.json().catch(() => null);
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("apiErrors.fallback.saveFailed")));
  },
  async adminAcceptances(q: string): Promise<{ acceptances: LegalAcceptanceApi[] }> {
    const res = await apiFetch(`/api/admin/legal-acceptances${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`);
    const json = await res.json().catch(() => null);
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("common.loadError")));
    return json;
  },
  /** DPA à accepter par l'organisateur avant d'utiliser l'application (lot 5), sinon `null`. */
  async pending(): Promise<{ pending: { dpa: { id: string; kind: "dpa"; version: string; effectiveOn: string; pdfSha256: string }; companyName: string } | null }> {
    const res = await apiFetch("/api/legal/pending");
    const json = await res.json().catch(() => null);
    if (!res.ok) throw new Error(apiErrorText(json, i18n.t("common.loadError")));
    return json;
  },
  async acceptDpa(versionId: string, language: string): Promise<void> {
    const res = await apiFetch("/api/legal/accept", { method: "POST", body: JSON.stringify({ versionId, language, accepted: true }) });
    const json = await res.json().catch(() => null);
    if (!res.ok) throw apiError(json, i18n.t("common.error"));
  },
  adminPdfUrl: (id: string) => `/api/admin/legal-documents/${id}/pdf`,
  publicPdfUrl: (id: string) => `/api/public/legal-documents/${id}/pdf`,
};

/**
 * « Prévenir le support » (décision 3.8) — appelé par le composant partagé `BlockingError`.
 * Aucun contenu saisi : l'écran, l'action, le message affiché, un code technique, le chemin de la
 * page (sans paramètres) et les identifiants Sentry capturés au clic.
 */
export const supportApi = {
  async report(input: { screen: SupportScreen; action: SupportAction; message: string; code: string; path: string; sentryEventIds: string[] }): Promise<{ sent: boolean; alreadyReported?: boolean }> {
    const res = await apiFetch("/api/support/report", { method: "POST", body: JSON.stringify(input) });
    const json = await res.json().catch(() => ({}));
    // Le statut accompagne l'erreur : 429 (limite du jour) n'a pas le même message que 503 / 5xx.
    if (!res.ok) throw Object.assign(new Error(i18n.t(res.status === 429 ? "blockingError.tooMany" : "blockingError.sendFailed")), { status: res.status });
    return json as { sent: boolean; alreadyReported?: boolean };
  },
};
